export type Json =
  | string
  | number
  | boolean
  | null
  | { [key: string]: Json | undefined }
  | Json[]

export type Database = {
  graphql_public: {
    Tables: {
      [_ in never]: never
    }
    Views: {
      [_ in never]: never
    }
    Functions: {
      graphql: {
        Args: {
          extensions?: Json
          operationName?: string
          query?: string
          variables?: Json
        }
        Returns: Json
      }
    }
    Enums: {
      [_ in never]: never
    }
    CompositeTypes: {
      [_ in never]: never
    }
  }
  public: {
    Tables: {
      agent_data_sources: {
        Row: {
          access_count: number
          created_at: string
          last_verified_at: string | null
          status: string
          updated_at: string
          url: string
          usage_notes: string | null
        }
        Insert: {
          access_count?: number
          created_at?: string
          last_verified_at?: string | null
          status?: string
          updated_at?: string
          url: string
          usage_notes?: string | null
        }
        Update: {
          access_count?: number
          created_at?: string
          last_verified_at?: string | null
          status?: string
          updated_at?: string
          url?: string
          usage_notes?: string | null
        }
        Relationships: []
      }
      agent_messages: {
        Row: {
          claimed_at: string | null
          content: Json
          cost_is_per_turn: boolean
          cost_usd: number | null
          created_at: string
          id: string
          input_tokens: number | null
          output_tokens: number | null
          role: Database["public"]["Enums"]["agent_message_role"]
          sequence_number: number
          status: string
          thread_id: string
          updated_at: string
        }
        Insert: {
          claimed_at?: string | null
          content: Json
          cost_is_per_turn?: boolean
          cost_usd?: number | null
          created_at?: string
          id?: string
          input_tokens?: number | null
          output_tokens?: number | null
          role: Database["public"]["Enums"]["agent_message_role"]
          sequence_number: number
          status?: string
          thread_id: string
          updated_at?: string
        }
        Update: {
          claimed_at?: string | null
          content?: Json
          cost_is_per_turn?: boolean
          cost_usd?: number | null
          created_at?: string
          id?: string
          input_tokens?: number | null
          output_tokens?: number | null
          role?: Database["public"]["Enums"]["agent_message_role"]
          sequence_number?: number
          status?: string
          thread_id?: string
          updated_at?: string
        }
        Relationships: [
          {
            foreignKeyName: "agent_messages_thread_id_fkey"
            columns: ["thread_id"]
            isOneToOne: false
            referencedRelation: "agent_threads"
            referencedColumns: ["id"]
          },
        ]
      }
      agent_threads: {
        Row: {
          cancellation_reason: string | null
          created_at: string
          created_by: string | null
          id: string
          interrupt_requested_at: string | null
          last_heartbeat_at: string | null
          map_id: string | null
          report_template_snapshot: Json | null
          sdk_cost_session_id: string | null
          sdk_cost_total_usd: number | null
          session_id: string | null
          source: string
          status: string
          title: string | null
          updated_at: string
          updated_by: string | null
          usage_core_layer_ids: string[]
          usage_cost_usd: number
          usage_input_tokens: number
          usage_output_tokens: number
          usage_tool_call_count: number
          workspace_id: string
        }
        Insert: {
          cancellation_reason?: string | null
          created_at?: string
          created_by?: string | null
          id?: string
          interrupt_requested_at?: string | null
          last_heartbeat_at?: string | null
          map_id?: string | null
          report_template_snapshot?: Json | null
          sdk_cost_session_id?: string | null
          sdk_cost_total_usd?: number | null
          session_id?: string | null
          source?: string
          status?: string
          title?: string | null
          updated_at?: string
          updated_by?: string | null
          usage_core_layer_ids?: string[]
          usage_cost_usd?: number
          usage_input_tokens?: number
          usage_output_tokens?: number
          usage_tool_call_count?: number
          workspace_id: string
        }
        Update: {
          cancellation_reason?: string | null
          created_at?: string
          created_by?: string | null
          id?: string
          interrupt_requested_at?: string | null
          last_heartbeat_at?: string | null
          map_id?: string | null
          report_template_snapshot?: Json | null
          sdk_cost_session_id?: string | null
          sdk_cost_total_usd?: number | null
          session_id?: string | null
          source?: string
          status?: string
          title?: string | null
          updated_at?: string
          updated_by?: string | null
          usage_core_layer_ids?: string[]
          usage_cost_usd?: number
          usage_input_tokens?: number
          usage_output_tokens?: number
          usage_tool_call_count?: number
          workspace_id?: string
        }
        Relationships: [
          {
            foreignKeyName: "agent_threads_created_by_fkey"
            columns: ["created_by"]
            isOneToOne: false
            referencedRelation: "user_profiles"
            referencedColumns: ["id"]
          },
          {
            foreignKeyName: "agent_threads_map_id_fkey"
            columns: ["map_id"]
            isOneToOne: false
            referencedRelation: "maps"
            referencedColumns: ["id"]
          },
          {
            foreignKeyName: "agent_threads_updated_by_fkey"
            columns: ["updated_by"]
            isOneToOne: false
            referencedRelation: "user_profiles"
            referencedColumns: ["id"]
          },
          {
            foreignKeyName: "agent_threads_workspace_id_fkey"
            columns: ["workspace_id"]
            isOneToOne: false
            referencedRelation: "workspaces"
            referencedColumns: ["id"]
          },
        ]
      }
      boundaries: {
        Row: {
          id: string
          name: string
          source: string
          source_layer: string
        }
        Insert: {
          id?: string
          name: string
          source: string
          source_layer: string
        }
        Update: {
          id?: string
          name?: string
          source?: string
          source_layer?: string
        }
        Relationships: []
      }
      boundary_features: {
        Row: {
          attributes: string[]
          boundary_id: string
          feature_id: string
          id: string
        }
        Insert: {
          attributes: string[]
          boundary_id: string
          feature_id: string
          id?: string
        }
        Update: {
          attributes?: string[]
          boundary_id?: string
          feature_id?: string
          id?: string
        }
        Relationships: [
          {
            foreignKeyName: "boundary_features_boundary_id_fkey"
            columns: ["boundary_id"]
            isOneToOne: false
            referencedRelation: "boundaries"
            referencedColumns: ["id"]
          },
        ]
      }
      core_layers: {
        Row: {
          canonical_layer_id: string | null
          created_at: string
          deprecation_reason: string | null
          description: string | null
          fields: Json | null
          id: string
          poi_exclusion_patterns: string[] | null
          processing_mode:
            | Database["public"]["Enums"]["core_layer_processing_mode"]
            | null
          record_count: number | null
          refreshed_at: string | null
          short_description: string | null
          source: string | null
          state_code: string | null
          tags: string[] | null
          title: string
          updated_at: string
          vintage: string | null
        }
        Insert: {
          canonical_layer_id?: string | null
          created_at?: string
          deprecation_reason?: string | null
          description?: string | null
          fields?: Json | null
          id: string
          poi_exclusion_patterns?: string[] | null
          processing_mode?:
            | Database["public"]["Enums"]["core_layer_processing_mode"]
            | null
          record_count?: number | null
          refreshed_at?: string | null
          short_description?: string | null
          source?: string | null
          state_code?: string | null
          tags?: string[] | null
          title: string
          updated_at?: string
          vintage?: string | null
        }
        Update: {
          canonical_layer_id?: string | null
          created_at?: string
          deprecation_reason?: string | null
          description?: string | null
          fields?: Json | null
          id?: string
          poi_exclusion_patterns?: string[] | null
          processing_mode?:
            | Database["public"]["Enums"]["core_layer_processing_mode"]
            | null
          record_count?: number | null
          refreshed_at?: string | null
          short_description?: string | null
          source?: string | null
          state_code?: string | null
          tags?: string[] | null
          title?: string
          updated_at?: string
          vintage?: string | null
        }
        Relationships: [
          {
            foreignKeyName: "core_layers_canonical_layer_id_fkey"
            columns: ["canonical_layer_id"]
            isOneToOne: false
            referencedRelation: "layers"
            referencedColumns: ["id"]
          },
        ]
      }
      diligence_export_fragment_cache: {
        Row: {
          change_key: string
          child_job_id: string
          created_at: string
          fragment_job_type: string
          id: string
          source_path: string
          workspace_id: string
        }
        Insert: {
          change_key: string
          child_job_id: string
          created_at?: string
          fragment_job_type: string
          id?: string
          source_path: string
          workspace_id: string
        }
        Update: {
          change_key?: string
          child_job_id?: string
          created_at?: string
          fragment_job_type?: string
          id?: string
          source_path?: string
          workspace_id?: string
        }
        Relationships: [
          {
            foreignKeyName: "diligence_export_fragment_cache_workspace_id_fkey"
            columns: ["workspace_id"]
            isOneToOne: false
            referencedRelation: "workspaces"
            referencedColumns: ["id"]
          },
        ]
      }
      free_report_generations: {
        Row: {
          counted_at_delete: boolean | null
          created_at: string
          deleted_at: string | null
          id: number
          layer_row_id: number
          pipeline_layer_id: string
          theme: string
          workspace_id: string
        }
        Insert: {
          counted_at_delete?: boolean | null
          created_at?: string
          deleted_at?: string | null
          id?: never
          layer_row_id: number
          pipeline_layer_id: string
          theme: string
          workspace_id: string
        }
        Update: {
          counted_at_delete?: boolean | null
          created_at?: string
          deleted_at?: string | null
          id?: never
          layer_row_id?: number
          pipeline_layer_id?: string
          theme?: string
          workspace_id?: string
        }
        Relationships: [
          {
            foreignKeyName: "free_report_generations_workspace_id_fkey"
            columns: ["workspace_id"]
            isOneToOne: false
            referencedRelation: "workspaces"
            referencedColumns: ["id"]
          },
        ]
      }
      free_report_theme_templates: {
        Row: {
          excluded_source_criterion_ids: string[]
          source_pipeline_id: string | null
          template_pipeline_layer_id: string
          theme: string
        }
        Insert: {
          excluded_source_criterion_ids?: string[]
          source_pipeline_id?: string | null
          template_pipeline_layer_id: string
          theme: string
        }
        Update: {
          excluded_source_criterion_ids?: string[]
          source_pipeline_id?: string | null
          template_pipeline_layer_id?: string
          theme?: string
        }
        Relationships: [
          {
            foreignKeyName: "free_report_theme_templates_source_pipeline_id_fkey"
            columns: ["source_pipeline_id"]
            isOneToOne: false
            referencedRelation: "layers"
            referencedColumns: ["id"]
          },
          {
            foreignKeyName: "free_report_theme_templates_template_pipeline_layer_id_fkey"
            columns: ["template_pipeline_layer_id"]
            isOneToOne: false
            referencedRelation: "layers"
            referencedColumns: ["id"]
          },
        ]
      }
      free_report_upload_events: {
        Row: {
          client_ip: unknown
          created_at: string
          id: number
        }
        Insert: {
          client_ip: unknown
          created_at?: string
          id?: never
        }
        Update: {
          client_ip?: unknown
          created_at?: string
          id?: never
        }
        Relationships: []
      }
      integration_connections: {
        Row: {
          connected_user_email: string | null
          created_at: string
          created_by: string | null
          id: string
          last_token_refresh_at: string | null
          name: string
          provider: Database["public"]["Enums"]["integration_provider"]
          provider_config: Json
          refresh_token_secret_id: string
          updated_at: string
          updated_by: string | null
          workspace_id: string
        }
        Insert: {
          connected_user_email?: string | null
          created_at?: string
          created_by?: string | null
          id?: string
          last_token_refresh_at?: string | null
          name: string
          provider: Database["public"]["Enums"]["integration_provider"]
          provider_config?: Json
          refresh_token_secret_id: string
          updated_at?: string
          updated_by?: string | null
          workspace_id: string
        }
        Update: {
          connected_user_email?: string | null
          created_at?: string
          created_by?: string | null
          id?: string
          last_token_refresh_at?: string | null
          name?: string
          provider?: Database["public"]["Enums"]["integration_provider"]
          provider_config?: Json
          refresh_token_secret_id?: string
          updated_at?: string
          updated_by?: string | null
          workspace_id?: string
        }
        Relationships: [
          {
            foreignKeyName: "integration_connections_created_by_fkey"
            columns: ["created_by"]
            isOneToOne: false
            referencedRelation: "user_profiles"
            referencedColumns: ["id"]
          },
          {
            foreignKeyName: "integration_connections_updated_by_fkey"
            columns: ["updated_by"]
            isOneToOne: false
            referencedRelation: "user_profiles"
            referencedColumns: ["id"]
          },
          {
            foreignKeyName: "integration_connections_workspace_id_fkey"
            columns: ["workspace_id"]
            isOneToOne: false
            referencedRelation: "workspaces"
            referencedColumns: ["id"]
          },
        ]
      }
      integration_record_mappings: {
        Row: {
          id: number
          layer_row_id: number
          source_last_modified: string | null
          source_record_id: string
          sync_config_id: string
        }
        Insert: {
          id?: number
          layer_row_id: number
          source_last_modified?: string | null
          source_record_id: string
          sync_config_id: string
        }
        Update: {
          id?: number
          layer_row_id?: number
          source_last_modified?: string | null
          source_record_id?: string
          sync_config_id?: string
        }
        Relationships: [
          {
            foreignKeyName: "integration_record_mappings_layer_row_id_fkey"
            columns: ["layer_row_id"]
            isOneToOne: false
            referencedRelation: "layer_rows"
            referencedColumns: ["id"]
          },
          {
            foreignKeyName: "integration_record_mappings_sync_config_id_fkey"
            columns: ["sync_config_id"]
            isOneToOne: false
            referencedRelation: "integration_sync_configs"
            referencedColumns: ["id"]
          },
        ]
      }
      integration_sync_configs: {
        Row: {
          connection_id: string
          created_at: string
          created_by: string | null
          error_message: string | null
          field_mapping: Json | null
          geomatch_config: Json | null
          id: string
          last_change_event_at: string | null
          last_sync_at: string | null
          layer_id: string
          selected_fields: string[] | null
          source_object: string
          source_object_label: string | null
          status: Database["public"]["Enums"]["sync_status"]
          sync_config: Json
          updated_at: string
          updated_by: string | null
        }
        Insert: {
          connection_id: string
          created_at?: string
          created_by?: string | null
          error_message?: string | null
          field_mapping?: Json | null
          geomatch_config?: Json | null
          id?: string
          last_change_event_at?: string | null
          last_sync_at?: string | null
          layer_id: string
          selected_fields?: string[] | null
          source_object: string
          source_object_label?: string | null
          status?: Database["public"]["Enums"]["sync_status"]
          sync_config?: Json
          updated_at?: string
          updated_by?: string | null
        }
        Update: {
          connection_id?: string
          created_at?: string
          created_by?: string | null
          error_message?: string | null
          field_mapping?: Json | null
          geomatch_config?: Json | null
          id?: string
          last_change_event_at?: string | null
          last_sync_at?: string | null
          layer_id?: string
          selected_fields?: string[] | null
          source_object?: string
          source_object_label?: string | null
          status?: Database["public"]["Enums"]["sync_status"]
          sync_config?: Json
          updated_at?: string
          updated_by?: string | null
        }
        Relationships: [
          {
            foreignKeyName: "integration_sync_configs_connection_id_fkey"
            columns: ["connection_id"]
            isOneToOne: false
            referencedRelation: "integration_connections"
            referencedColumns: ["id"]
          },
          {
            foreignKeyName: "integration_sync_configs_created_by_fkey"
            columns: ["created_by"]
            isOneToOne: false
            referencedRelation: "user_profiles"
            referencedColumns: ["id"]
          },
          {
            foreignKeyName: "integration_sync_configs_layer_id_fkey"
            columns: ["layer_id"]
            isOneToOne: true
            referencedRelation: "layers"
            referencedColumns: ["id"]
          },
          {
            foreignKeyName: "integration_sync_configs_updated_by_fkey"
            columns: ["updated_by"]
            isOneToOne: false
            referencedRelation: "user_profiles"
            referencedColumns: ["id"]
          },
        ]
      }
      jobs: {
        Row: {
          created_at: string
          created_by: string | null
          description: string | null
          error_message: string | null
          id: string
          layer_id: string | null
          map_id: string | null
          name: string
          parent_job_id: string | null
          payload: Json
          result: Json | null
          status: Database["public"]["Enums"]["job_status"]
          updated_at: string
          updated_by: string | null
          workspace_id: string | null
        }
        Insert: {
          created_at?: string
          created_by?: string | null
          description?: string | null
          error_message?: string | null
          id?: string
          layer_id?: string | null
          map_id?: string | null
          name: string
          parent_job_id?: string | null
          payload: Json
          result?: Json | null
          status?: Database["public"]["Enums"]["job_status"]
          updated_at?: string
          updated_by?: string | null
          workspace_id?: string | null
        }
        Update: {
          created_at?: string
          created_by?: string | null
          description?: string | null
          error_message?: string | null
          id?: string
          layer_id?: string | null
          map_id?: string | null
          name?: string
          parent_job_id?: string | null
          payload?: Json
          result?: Json | null
          status?: Database["public"]["Enums"]["job_status"]
          updated_at?: string
          updated_by?: string | null
          workspace_id?: string | null
        }
        Relationships: [
          {
            foreignKeyName: "jobs_created_by_fkey"
            columns: ["created_by"]
            isOneToOne: false
            referencedRelation: "user_profiles"
            referencedColumns: ["id"]
          },
          {
            foreignKeyName: "jobs_map_id_fkey"
            columns: ["map_id"]
            isOneToOne: false
            referencedRelation: "maps"
            referencedColumns: ["id"]
          },
          {
            foreignKeyName: "jobs_parent_job_id_fkey"
            columns: ["parent_job_id"]
            isOneToOne: false
            referencedRelation: "jobs"
            referencedColumns: ["id"]
          },
          {
            foreignKeyName: "jobs_updated_by_fkey"
            columns: ["updated_by"]
            isOneToOne: false
            referencedRelation: "user_profiles"
            referencedColumns: ["id"]
          },
          {
            foreignKeyName: "jobs_workspace_id_fkey"
            columns: ["workspace_id"]
            isOneToOne: false
            referencedRelation: "workspaces"
            referencedColumns: ["id"]
          },
        ]
      }
      landowner_research: {
        Row: {
          business_career: Json | null
          business_description: Json | null
          civic_activity: Json | null
          contact_info: Json | null
          created_at: string | null
          encumbrances: Json | null
          id: string
          linkedin_page: Json | null
          owner_name: string | null
          ownership_history: Json | null
          ownership_type: Json | null
          parcel_info: Json | null
          public_filings: Json | null
          research_result: string | null
          robust_id: string
          updated_at: string | null
          website: Json | null
          workspace_id: string
        }
        Insert: {
          business_career?: Json | null
          business_description?: Json | null
          civic_activity?: Json | null
          contact_info?: Json | null
          created_at?: string | null
          encumbrances?: Json | null
          id?: string
          linkedin_page?: Json | null
          owner_name?: string | null
          ownership_history?: Json | null
          ownership_type?: Json | null
          parcel_info?: Json | null
          public_filings?: Json | null
          research_result?: string | null
          robust_id: string
          updated_at?: string | null
          website?: Json | null
          workspace_id: string
        }
        Update: {
          business_career?: Json | null
          business_description?: Json | null
          civic_activity?: Json | null
          contact_info?: Json | null
          created_at?: string | null
          encumbrances?: Json | null
          id?: string
          linkedin_page?: Json | null
          owner_name?: string | null
          ownership_history?: Json | null
          ownership_type?: Json | null
          parcel_info?: Json | null
          public_filings?: Json | null
          research_result?: string | null
          robust_id?: string
          updated_at?: string | null
          website?: Json | null
          workspace_id?: string
        }
        Relationships: [
          {
            foreignKeyName: "landowner_research_workspace_id_fkey"
            columns: ["workspace_id"]
            isOneToOne: false
            referencedRelation: "workspaces"
            referencedColumns: ["id"]
          },
        ]
      }
      layer_builder_rows: {
        Row: {
          additional_context: string | null
          agent_thread_id: string | null
          created_at: string
          created_by: string | null
          data_source: string | null
          error_message: string | null
          example_url: string | null
          geography: string | null
          geography_column: string | null
          id: string
          layer_id: string | null
          layer_name: string
          layer_type: string | null
          notes: string | null
          review_status: Database["public"]["Enums"]["layer_builder_review_status"]
          row_index: number
          source_type: string | null
          status: Database["public"]["Enums"]["layer_builder_row_status"]
          updated_at: string
          updated_by: string | null
          upload_id: string
          workspace_id: string
        }
        Insert: {
          additional_context?: string | null
          agent_thread_id?: string | null
          created_at?: string
          created_by?: string | null
          data_source?: string | null
          error_message?: string | null
          example_url?: string | null
          geography?: string | null
          geography_column?: string | null
          id?: string
          layer_id?: string | null
          layer_name: string
          layer_type?: string | null
          notes?: string | null
          review_status?: Database["public"]["Enums"]["layer_builder_review_status"]
          row_index: number
          source_type?: string | null
          status?: Database["public"]["Enums"]["layer_builder_row_status"]
          updated_at?: string
          updated_by?: string | null
          upload_id: string
          workspace_id: string
        }
        Update: {
          additional_context?: string | null
          agent_thread_id?: string | null
          created_at?: string
          created_by?: string | null
          data_source?: string | null
          error_message?: string | null
          example_url?: string | null
          geography?: string | null
          geography_column?: string | null
          id?: string
          layer_id?: string | null
          layer_name?: string
          layer_type?: string | null
          notes?: string | null
          review_status?: Database["public"]["Enums"]["layer_builder_review_status"]
          row_index?: number
          source_type?: string | null
          status?: Database["public"]["Enums"]["layer_builder_row_status"]
          updated_at?: string
          updated_by?: string | null
          upload_id?: string
          workspace_id?: string
        }
        Relationships: [
          {
            foreignKeyName: "layer_builder_rows_agent_thread_id_fkey"
            columns: ["agent_thread_id"]
            isOneToOne: false
            referencedRelation: "agent_threads"
            referencedColumns: ["id"]
          },
          {
            foreignKeyName: "layer_builder_rows_created_by_fkey"
            columns: ["created_by"]
            isOneToOne: false
            referencedRelation: "user_profiles"
            referencedColumns: ["id"]
          },
          {
            foreignKeyName: "layer_builder_rows_layer_id_fkey"
            columns: ["layer_id"]
            isOneToOne: false
            referencedRelation: "layers"
            referencedColumns: ["id"]
          },
          {
            foreignKeyName: "layer_builder_rows_updated_by_fkey"
            columns: ["updated_by"]
            isOneToOne: false
            referencedRelation: "user_profiles"
            referencedColumns: ["id"]
          },
          {
            foreignKeyName: "layer_builder_rows_upload_id_fkey"
            columns: ["upload_id"]
            isOneToOne: false
            referencedRelation: "layer_builder_uploads"
            referencedColumns: ["id"]
          },
          {
            foreignKeyName: "layer_builder_rows_workspace_id_fkey"
            columns: ["workspace_id"]
            isOneToOne: false
            referencedRelation: "workspaces"
            referencedColumns: ["id"]
          },
        ]
      }
      layer_builder_uploads: {
        Row: {
          created_at: string
          created_by: string | null
          filename: string
          id: string
          map_id: string
          row_count: number
          updated_at: string
          updated_by: string | null
          workspace_id: string
        }
        Insert: {
          created_at?: string
          created_by?: string | null
          filename: string
          id?: string
          map_id: string
          row_count?: number
          updated_at?: string
          updated_by?: string | null
          workspace_id: string
        }
        Update: {
          created_at?: string
          created_by?: string | null
          filename?: string
          id?: string
          map_id?: string
          row_count?: number
          updated_at?: string
          updated_by?: string | null
          workspace_id?: string
        }
        Relationships: [
          {
            foreignKeyName: "layer_builder_uploads_created_by_fkey"
            columns: ["created_by"]
            isOneToOne: false
            referencedRelation: "user_profiles"
            referencedColumns: ["id"]
          },
          {
            foreignKeyName: "layer_builder_uploads_map_id_fkey"
            columns: ["map_id"]
            isOneToOne: false
            referencedRelation: "maps"
            referencedColumns: ["id"]
          },
          {
            foreignKeyName: "layer_builder_uploads_updated_by_fkey"
            columns: ["updated_by"]
            isOneToOne: false
            referencedRelation: "user_profiles"
            referencedColumns: ["id"]
          },
          {
            foreignKeyName: "layer_builder_uploads_workspace_id_fkey"
            columns: ["workspace_id"]
            isOneToOne: false
            referencedRelation: "workspaces"
            referencedColumns: ["id"]
          },
        ]
      }
      layer_changes: {
        Row: {
          created_at: string
          event_data: Json | null
          event_type: string
          id: number
          layer_id: string
        }
        Insert: {
          created_at?: string
          event_data?: Json | null
          event_type: string
          id?: number
          layer_id: string
        }
        Update: {
          created_at?: string
          event_data?: Json | null
          event_type?: string
          id?: number
          layer_id?: string
        }
        Relationships: [
          {
            foreignKeyName: "layer_changes_layer_id_fkey"
            columns: ["layer_id"]
            isOneToOne: false
            referencedRelation: "layers"
            referencedColumns: ["id"]
          },
        ]
      }
      layer_columns: {
        Row: {
          column_name: string
          data_type: string
          display_name: string | null
          id: number
          is_read_only: boolean
          layer_id: string
          position: number
        }
        Insert: {
          column_name: string
          data_type?: string
          display_name?: string | null
          id?: number
          is_read_only?: boolean
          layer_id: string
          position: number
        }
        Update: {
          column_name?: string
          data_type?: string
          display_name?: string | null
          id?: number
          is_read_only?: boolean
          layer_id?: string
          position?: number
        }
        Relationships: [
          {
            foreignKeyName: "layer_columns_layer_id_fkey"
            columns: ["layer_id"]
            isOneToOne: false
            referencedRelation: "layers"
            referencedColumns: ["id"]
          },
        ]
      }
      layer_folders: {
        Row: {
          created_at: string
          created_by: string | null
          description: string | null
          id: string
          job_id: string | null
          name: string
          updated_at: string
          updated_by: string | null
          workspace_id: string
        }
        Insert: {
          created_at?: string
          created_by?: string | null
          description?: string | null
          id?: string
          job_id?: string | null
          name: string
          updated_at?: string
          updated_by?: string | null
          workspace_id: string
        }
        Update: {
          created_at?: string
          created_by?: string | null
          description?: string | null
          id?: string
          job_id?: string | null
          name?: string
          updated_at?: string
          updated_by?: string | null
          workspace_id?: string
        }
        Relationships: [
          {
            foreignKeyName: "layer_folders_created_by_fkey"
            columns: ["created_by"]
            isOneToOne: false
            referencedRelation: "user_profiles"
            referencedColumns: ["id"]
          },
          {
            foreignKeyName: "layer_folders_job_id_fkey"
            columns: ["job_id"]
            isOneToOne: false
            referencedRelation: "jobs"
            referencedColumns: ["id"]
          },
          {
            foreignKeyName: "layer_folders_updated_by_fkey"
            columns: ["updated_by"]
            isOneToOne: false
            referencedRelation: "user_profiles"
            referencedColumns: ["id"]
          },
          {
            foreignKeyName: "layer_folders_workspace_id_fkey"
            columns: ["workspace_id"]
            isOneToOne: false
            referencedRelation: "workspaces"
            referencedColumns: ["id"]
          },
        ]
      }
      layer_primary_threads: {
        Row: {
          created_at: string
          created_by: string | null
          layer_id: string
          thread_id: string
          workspace_id: string
        }
        Insert: {
          created_at?: string
          created_by?: string | null
          layer_id: string
          thread_id: string
          workspace_id: string
        }
        Update: {
          created_at?: string
          created_by?: string | null
          layer_id?: string
          thread_id?: string
          workspace_id?: string
        }
        Relationships: [
          {
            foreignKeyName: "layer_primary_threads_created_by_fkey"
            columns: ["created_by"]
            isOneToOne: false
            referencedRelation: "user_profiles"
            referencedColumns: ["id"]
          },
          {
            foreignKeyName: "layer_primary_threads_layer_id_fkey"
            columns: ["layer_id"]
            isOneToOne: false
            referencedRelation: "layers"
            referencedColumns: ["id"]
          },
          {
            foreignKeyName: "layer_primary_threads_thread_id_fkey"
            columns: ["thread_id"]
            isOneToOne: true
            referencedRelation: "agent_threads"
            referencedColumns: ["id"]
          },
          {
            foreignKeyName: "layer_primary_threads_workspace_id_fkey"
            columns: ["workspace_id"]
            isOneToOne: false
            referencedRelation: "workspaces"
            referencedColumns: ["id"]
          },
        ]
      }
      layer_revision_threads: {
        Row: {
          created_at: string
          revision_id: string
          thread_id: string
        }
        Insert: {
          created_at?: string
          revision_id: string
          thread_id: string
        }
        Update: {
          created_at?: string
          revision_id?: string
          thread_id?: string
        }
        Relationships: [
          {
            foreignKeyName: "layer_revision_threads_revision_id_fkey"
            columns: ["revision_id"]
            isOneToOne: false
            referencedRelation: "layer_revisions"
            referencedColumns: ["id"]
          },
          {
            foreignKeyName: "layer_revision_threads_thread_id_fkey"
            columns: ["thread_id"]
            isOneToOne: false
            referencedRelation: "agent_threads"
            referencedColumns: ["id"]
          },
        ]
      }
      layer_revisions: {
        Row: {
          bbox: unknown
          created_at: string
          created_by: string | null
          description: string | null
          geometry_type:
            | Database["public"]["Enums"]["layer_geometry_type"]
            | null
          id: string
          job_id: string | null
          layer_id: string
          max_zoom: number | null
          min_zoom: number | null
          revision_number: number
          source_filename: string | null
          stats: Json | null
        }
        Insert: {
          bbox?: unknown
          created_at?: string
          created_by?: string | null
          description?: string | null
          geometry_type?:
            | Database["public"]["Enums"]["layer_geometry_type"]
            | null
          id?: string
          job_id?: string | null
          layer_id: string
          max_zoom?: number | null
          min_zoom?: number | null
          revision_number: number
          source_filename?: string | null
          stats?: Json | null
        }
        Update: {
          bbox?: unknown
          created_at?: string
          created_by?: string | null
          description?: string | null
          geometry_type?:
            | Database["public"]["Enums"]["layer_geometry_type"]
            | null
          id?: string
          job_id?: string | null
          layer_id?: string
          max_zoom?: number | null
          min_zoom?: number | null
          revision_number?: number
          source_filename?: string | null
          stats?: Json | null
        }
        Relationships: [
          {
            foreignKeyName: "layer_revisions_created_by_fkey"
            columns: ["created_by"]
            isOneToOne: false
            referencedRelation: "user_profiles"
            referencedColumns: ["id"]
          },
          {
            foreignKeyName: "layer_revisions_job_id_fkey"
            columns: ["job_id"]
            isOneToOne: false
            referencedRelation: "jobs"
            referencedColumns: ["id"]
          },
          {
            foreignKeyName: "layer_revisions_layer_id_fkey"
            columns: ["layer_id"]
            isOneToOne: false
            referencedRelation: "layers"
            referencedColumns: ["id"]
          },
        ]
      }
      layer_rows: {
        Row: {
          feature_id: string | null
          feature_match:
            | Database["public"]["Enums"]["feature_match_status"]
            | null
          geometry: unknown
          id: number
          layer_id: string
        }
        Insert: {
          feature_id?: string | null
          feature_match?:
            | Database["public"]["Enums"]["feature_match_status"]
            | null
          geometry?: unknown
          id?: number
          layer_id: string
        }
        Update: {
          feature_id?: string | null
          feature_match?:
            | Database["public"]["Enums"]["feature_match_status"]
            | null
          geometry?: unknown
          id?: number
          layer_id?: string
        }
        Relationships: [
          {
            foreignKeyName: "layer_rows_layer_id_fkey"
            columns: ["layer_id"]
            isOneToOne: false
            referencedRelation: "layers"
            referencedColumns: ["id"]
          },
        ]
      }
      layer_values: {
        Row: {
          column_id: number
          created_at: string
          id: number
          row_id: number
          updated_at: string
          value: Json | null
        }
        Insert: {
          column_id: number
          created_at?: string
          id?: number
          row_id: number
          updated_at?: string
          value?: Json | null
        }
        Update: {
          column_id?: number
          created_at?: string
          id?: number
          row_id?: number
          updated_at?: string
          value?: Json | null
        }
        Relationships: [
          {
            foreignKeyName: "layer_values_column_id_fkey"
            columns: ["column_id"]
            isOneToOne: false
            referencedRelation: "layer_columns"
            referencedColumns: ["id"]
          },
          {
            foreignKeyName: "layer_values_row_id_fkey"
            columns: ["row_id"]
            isOneToOne: false
            referencedRelation: "layer_rows"
            referencedColumns: ["id"]
          },
        ]
      }
      layers: {
        Row: {
          bbox: unknown
          boundary_id: string | null
          created_at: string
          created_by: string | null
          current_revision_id: string | null
          default_mapbox_style_data: Json | null
          deleted_at: string | null
          deleted_by: string | null
          description: string | null
          folder_id: string | null
          geometry_type:
            | Database["public"]["Enums"]["layer_geometry_type"]
            | null
          id: string
          integration_sync_config_id: string | null
          is_internal: boolean
          job_id: string | null
          matched_columns: Json | null
          max_zoom: number | null
          min_zoom: number | null
          raster_algorithm: string | null
          raster_dataset_id: string | null
          short_description: string | null
          source_filename: string | null
          stats: Json | null
          status: Database["public"]["Enums"]["layer_status"]
          tileset_format: Database["public"]["Enums"]["tileset_format"] | null
          title: string
          type: Database["public"]["Enums"]["layer_type"]
          updated_at: string
          updated_by: string | null
          workspace_id: string | null
        }
        Insert: {
          bbox?: unknown
          boundary_id?: string | null
          created_at?: string
          created_by?: string | null
          current_revision_id?: string | null
          default_mapbox_style_data?: Json | null
          deleted_at?: string | null
          deleted_by?: string | null
          description?: string | null
          folder_id?: string | null
          geometry_type?:
            | Database["public"]["Enums"]["layer_geometry_type"]
            | null
          id?: string
          integration_sync_config_id?: string | null
          is_internal?: boolean
          job_id?: string | null
          matched_columns?: Json | null
          max_zoom?: number | null
          min_zoom?: number | null
          raster_algorithm?: string | null
          raster_dataset_id?: string | null
          short_description?: string | null
          source_filename?: string | null
          stats?: Json | null
          status?: Database["public"]["Enums"]["layer_status"]
          tileset_format?: Database["public"]["Enums"]["tileset_format"] | null
          title: string
          type?: Database["public"]["Enums"]["layer_type"]
          updated_at?: string
          updated_by?: string | null
          workspace_id?: string | null
        }
        Update: {
          bbox?: unknown
          boundary_id?: string | null
          created_at?: string
          created_by?: string | null
          current_revision_id?: string | null
          default_mapbox_style_data?: Json | null
          deleted_at?: string | null
          deleted_by?: string | null
          description?: string | null
          folder_id?: string | null
          geometry_type?:
            | Database["public"]["Enums"]["layer_geometry_type"]
            | null
          id?: string
          integration_sync_config_id?: string | null
          is_internal?: boolean
          job_id?: string | null
          matched_columns?: Json | null
          max_zoom?: number | null
          min_zoom?: number | null
          raster_algorithm?: string | null
          raster_dataset_id?: string | null
          short_description?: string | null
          source_filename?: string | null
          stats?: Json | null
          status?: Database["public"]["Enums"]["layer_status"]
          tileset_format?: Database["public"]["Enums"]["tileset_format"] | null
          title?: string
          type?: Database["public"]["Enums"]["layer_type"]
          updated_at?: string
          updated_by?: string | null
          workspace_id?: string | null
        }
        Relationships: [
          {
            foreignKeyName: "fk_layers_folder"
            columns: ["folder_id", "workspace_id"]
            isOneToOne: false
            referencedRelation: "layer_folders"
            referencedColumns: ["id", "workspace_id"]
          },
          {
            foreignKeyName: "layers_boundary_id_fkey"
            columns: ["boundary_id"]
            isOneToOne: false
            referencedRelation: "boundaries"
            referencedColumns: ["id"]
          },
          {
            foreignKeyName: "layers_created_by_fkey"
            columns: ["created_by"]
            isOneToOne: false
            referencedRelation: "user_profiles"
            referencedColumns: ["id"]
          },
          {
            foreignKeyName: "layers_current_revision_id_fkey"
            columns: ["current_revision_id"]
            isOneToOne: false
            referencedRelation: "layer_revisions"
            referencedColumns: ["id"]
          },
          {
            foreignKeyName: "layers_deleted_by_fkey"
            columns: ["deleted_by"]
            isOneToOne: false
            referencedRelation: "user_profiles"
            referencedColumns: ["id"]
          },
          {
            foreignKeyName: "layers_fk_integration_sync_config_id"
            columns: ["integration_sync_config_id"]
            isOneToOne: false
            referencedRelation: "integration_sync_configs"
            referencedColumns: ["id"]
          },
          {
            foreignKeyName: "layers_job_id_fkey"
            columns: ["job_id"]
            isOneToOne: false
            referencedRelation: "jobs"
            referencedColumns: ["id"]
          },
          {
            foreignKeyName: "layers_raster_dataset_id_fkey"
            columns: ["raster_dataset_id"]
            isOneToOne: false
            referencedRelation: "raster_datasets"
            referencedColumns: ["id"]
          },
          {
            foreignKeyName: "layers_updated_by_fkey"
            columns: ["updated_by"]
            isOneToOne: false
            referencedRelation: "user_profiles"
            referencedColumns: ["id"]
          },
          {
            foreignKeyName: "layers_workspace_id_fkey"
            columns: ["workspace_id"]
            isOneToOne: false
            referencedRelation: "workspaces"
            referencedColumns: ["id"]
          },
        ]
      }
      map_ai_images: {
        Row: {
          created_at: string
          created_by: string | null
          generation_time_ms: number | null
          id: string
          image_url: string
          is_active: boolean
          map_id: string
          prompt_used: string
          storage_path: string
          updated_at: string
          updated_by: string | null
          workspace_id: string
        }
        Insert: {
          created_at?: string
          created_by?: string | null
          generation_time_ms?: number | null
          id?: string
          image_url: string
          is_active?: boolean
          map_id: string
          prompt_used: string
          storage_path: string
          updated_at?: string
          updated_by?: string | null
          workspace_id: string
        }
        Update: {
          created_at?: string
          created_by?: string | null
          generation_time_ms?: number | null
          id?: string
          image_url?: string
          is_active?: boolean
          map_id?: string
          prompt_used?: string
          storage_path?: string
          updated_at?: string
          updated_by?: string | null
          workspace_id?: string
        }
        Relationships: [
          {
            foreignKeyName: "map_ai_images_created_by_fkey"
            columns: ["created_by"]
            isOneToOne: false
            referencedRelation: "user_profiles"
            referencedColumns: ["id"]
          },
          {
            foreignKeyName: "map_ai_images_map_id_fkey"
            columns: ["map_id"]
            isOneToOne: false
            referencedRelation: "maps"
            referencedColumns: ["id"]
          },
          {
            foreignKeyName: "map_ai_images_updated_by_fkey"
            columns: ["updated_by"]
            isOneToOne: false
            referencedRelation: "user_profiles"
            referencedColumns: ["id"]
          },
          {
            foreignKeyName: "map_ai_images_workspace_id_fkey"
            columns: ["workspace_id"]
            isOneToOne: false
            referencedRelation: "workspaces"
            referencedColumns: ["id"]
          },
        ]
      }
      map_embeds: {
        Row: {
          allowed_domain: string
          created_at: string
          created_by: string | null
          id: string
          initial_center: Json
          initial_zoom: number
          is_enabled: boolean
          layer_mode: string
          layer_visibility: Json
          map_id: string
          updated_at: string
          workspace_id: string
        }
        Insert: {
          allowed_domain: string
          created_at?: string
          created_by?: string | null
          id: string
          initial_center?: Json
          initial_zoom?: number
          is_enabled?: boolean
          layer_mode?: string
          layer_visibility?: Json
          map_id: string
          updated_at?: string
          workspace_id: string
        }
        Update: {
          allowed_domain?: string
          created_at?: string
          created_by?: string | null
          id?: string
          initial_center?: Json
          initial_zoom?: number
          is_enabled?: boolean
          layer_mode?: string
          layer_visibility?: Json
          map_id?: string
          updated_at?: string
          workspace_id?: string
        }
        Relationships: [
          {
            foreignKeyName: "map_embeds_created_by_fkey"
            columns: ["created_by"]
            isOneToOne: false
            referencedRelation: "user_profiles"
            referencedColumns: ["id"]
          },
          {
            foreignKeyName: "map_embeds_map_id_fkey"
            columns: ["map_id"]
            isOneToOne: false
            referencedRelation: "maps"
            referencedColumns: ["id"]
          },
          {
            foreignKeyName: "map_embeds_workspace_id_fkey"
            columns: ["workspace_id"]
            isOneToOne: false
            referencedRelation: "workspaces"
            referencedColumns: ["id"]
          },
        ]
      }
      map_layers: {
        Row: {
          created_at: string
          created_by: string | null
          group_id: string | null
          id: number
          layer_id: string
          map_id: string
          mapbox_style_data: Json | null
          updated_at: string
          updated_by: string | null
        }
        Insert: {
          created_at?: string
          created_by?: string | null
          group_id?: string | null
          id?: number
          layer_id: string
          map_id: string
          mapbox_style_data?: Json | null
          updated_at?: string
          updated_by?: string | null
        }
        Update: {
          created_at?: string
          created_by?: string | null
          group_id?: string | null
          id?: number
          layer_id?: string
          map_id?: string
          mapbox_style_data?: Json | null
          updated_at?: string
          updated_by?: string | null
        }
        Relationships: [
          {
            foreignKeyName: "map_layers_created_by_fkey"
            columns: ["created_by"]
            isOneToOne: false
            referencedRelation: "user_profiles"
            referencedColumns: ["id"]
          },
          {
            foreignKeyName: "map_layers_group_id_fkey"
            columns: ["group_id"]
            isOneToOne: false
            referencedRelation: "map_layers_groups"
            referencedColumns: ["id"]
          },
          {
            foreignKeyName: "map_layers_group_ref"
            columns: ["group_id", "map_id"]
            isOneToOne: false
            referencedRelation: "map_layers_groups"
            referencedColumns: ["id", "map_id"]
          },
          {
            foreignKeyName: "map_layers_layer_id_fkey"
            columns: ["layer_id"]
            isOneToOne: false
            referencedRelation: "layers"
            referencedColumns: ["id"]
          },
          {
            foreignKeyName: "map_layers_map_id_fkey"
            columns: ["map_id"]
            isOneToOne: false
            referencedRelation: "maps"
            referencedColumns: ["id"]
          },
          {
            foreignKeyName: "map_layers_updated_by_fkey"
            columns: ["updated_by"]
            isOneToOne: false
            referencedRelation: "user_profiles"
            referencedColumns: ["id"]
          },
        ]
      }
      map_layers_groups: {
        Row: {
          created_at: string
          created_by: string | null
          id: string
          map_id: string
          title: string
          updated_at: string
          updated_by: string | null
        }
        Insert: {
          created_at?: string
          created_by?: string | null
          id?: string
          map_id: string
          title: string
          updated_at?: string
          updated_by?: string | null
        }
        Update: {
          created_at?: string
          created_by?: string | null
          id?: string
          map_id?: string
          title?: string
          updated_at?: string
          updated_by?: string | null
        }
        Relationships: [
          {
            foreignKeyName: "map_layers_groups_created_by_fkey"
            columns: ["created_by"]
            isOneToOne: false
            referencedRelation: "user_profiles"
            referencedColumns: ["id"]
          },
          {
            foreignKeyName: "map_layers_groups_map_id_fkey"
            columns: ["map_id"]
            isOneToOne: false
            referencedRelation: "maps"
            referencedColumns: ["id"]
          },
          {
            foreignKeyName: "map_layers_groups_updated_by_fkey"
            columns: ["updated_by"]
            isOneToOne: false
            referencedRelation: "user_profiles"
            referencedColumns: ["id"]
          },
        ]
      }
      maps: {
        Row: {
          created_at: string
          created_by: string | null
          description: string | null
          id: string
          is_internal: boolean
          title: string
          updated_at: string
          updated_by: string | null
          workspace_id: string
        }
        Insert: {
          created_at?: string
          created_by?: string | null
          description?: string | null
          id?: string
          is_internal?: boolean
          title: string
          updated_at?: string
          updated_by?: string | null
          workspace_id: string
        }
        Update: {
          created_at?: string
          created_by?: string | null
          description?: string | null
          id?: string
          is_internal?: boolean
          title?: string
          updated_at?: string
          updated_by?: string | null
          workspace_id?: string
        }
        Relationships: [
          {
            foreignKeyName: "maps_created_by_fkey"
            columns: ["created_by"]
            isOneToOne: false
            referencedRelation: "user_profiles"
            referencedColumns: ["id"]
          },
          {
            foreignKeyName: "maps_updated_by_fkey"
            columns: ["updated_by"]
            isOneToOne: false
            referencedRelation: "user_profiles"
            referencedColumns: ["id"]
          },
          {
            foreignKeyName: "maps_workspace_id_fkey"
            columns: ["workspace_id"]
            isOneToOne: false
            referencedRelation: "workspaces"
            referencedColumns: ["id"]
          },
        ]
      }
      pipeline_search_sites: {
        Row: {
          id: string
          injected_at: string
          layer_row_id: number
          search_id: string
        }
        Insert: {
          id?: string
          injected_at?: string
          layer_row_id: number
          search_id: string
        }
        Update: {
          id?: string
          injected_at?: string
          layer_row_id?: number
          search_id?: string
        }
        Relationships: [
          {
            foreignKeyName: "pipeline_search_sites_layer_row_id_fkey"
            columns: ["layer_row_id"]
            isOneToOne: false
            referencedRelation: "layer_rows"
            referencedColumns: ["id"]
          },
          {
            foreignKeyName: "pipeline_search_sites_search_id_fkey"
            columns: ["search_id"]
            isOneToOne: false
            referencedRelation: "pipeline_searches"
            referencedColumns: ["id"]
          },
        ]
      }
      pipeline_searches: {
        Row: {
          created_at: string
          created_by: string | null
          deleted_at: string | null
          id: string
          layer_id: string | null
          result_layer_id: string | null
          thread_id: string | null
          title: string | null
          updated_at: string
          updated_by: string | null
          workspace_id: string
        }
        Insert: {
          created_at?: string
          created_by?: string | null
          deleted_at?: string | null
          id?: string
          layer_id?: string | null
          result_layer_id?: string | null
          thread_id?: string | null
          title?: string | null
          updated_at?: string
          updated_by?: string | null
          workspace_id: string
        }
        Update: {
          created_at?: string
          created_by?: string | null
          deleted_at?: string | null
          id?: string
          layer_id?: string | null
          result_layer_id?: string | null
          thread_id?: string | null
          title?: string | null
          updated_at?: string
          updated_by?: string | null
          workspace_id?: string
        }
        Relationships: [
          {
            foreignKeyName: "pipeline_searches_created_by_fkey"
            columns: ["created_by"]
            isOneToOne: false
            referencedRelation: "user_profiles"
            referencedColumns: ["id"]
          },
          {
            foreignKeyName: "pipeline_searches_layer_id_fkey"
            columns: ["layer_id"]
            isOneToOne: false
            referencedRelation: "layers"
            referencedColumns: ["id"]
          },
          {
            foreignKeyName: "pipeline_searches_result_layer_id_fkey"
            columns: ["result_layer_id"]
            isOneToOne: false
            referencedRelation: "layers"
            referencedColumns: ["id"]
          },
          {
            foreignKeyName: "pipeline_searches_thread_id_fkey"
            columns: ["thread_id"]
            isOneToOne: false
            referencedRelation: "agent_threads"
            referencedColumns: ["id"]
          },
          {
            foreignKeyName: "pipeline_searches_updated_by_fkey"
            columns: ["updated_by"]
            isOneToOne: false
            referencedRelation: "user_profiles"
            referencedColumns: ["id"]
          },
          {
            foreignKeyName: "pipeline_searches_workspace_id_fkey"
            columns: ["workspace_id"]
            isOneToOne: false
            referencedRelation: "workspaces"
            referencedColumns: ["id"]
          },
        ]
      }
      raster_dataset_assets: {
        Row: {
          created_at: string
          dataset_id: string
          geometry: unknown
          id: number
          source_id: string
        }
        Insert: {
          created_at?: string
          dataset_id: string
          geometry: unknown
          id?: never
          source_id: string
        }
        Update: {
          created_at?: string
          dataset_id?: string
          geometry?: unknown
          id?: never
          source_id?: string
        }
        Relationships: [
          {
            foreignKeyName: "raster_dataset_assets_dataset_id_fkey"
            columns: ["dataset_id"]
            isOneToOne: false
            referencedRelation: "raster_datasets"
            referencedColumns: ["id"]
          },
        ]
      }
      raster_datasets: {
        Row: {
          cog_uri_prefix: string
          created_at: string
          id: string
          kind: Database["public"]["Enums"]["raster_dataset_kind"]
          native_max_zoom: number
          refreshed_at: string | null
          source: string | null
          title: string
          updated_at: string
        }
        Insert: {
          cog_uri_prefix: string
          created_at?: string
          id: string
          kind: Database["public"]["Enums"]["raster_dataset_kind"]
          native_max_zoom: number
          refreshed_at?: string | null
          source?: string | null
          title: string
          updated_at?: string
        }
        Update: {
          cog_uri_prefix?: string
          created_at?: string
          id?: string
          kind?: Database["public"]["Enums"]["raster_dataset_kind"]
          native_max_zoom?: number
          refreshed_at?: string | null
          source?: string | null
          title?: string
          updated_at?: string
        }
        Relationships: []
      }
      research_ahj: {
        Row: {
          area_name: string
          county_fips: string
          cousub_fips: string | null
          discovery_hints: string[] | null
          endonym_confidence:
            | Database["public"]["Enums"]["endonym_confidence_enum"]
            | null
          geometry_updated_at: string | null
          geometry_uri: string | null
          government_type: Database["public"]["Enums"]["government_type_enum"]
          has_zoning_ordinance: boolean | null
          is_incorporated: boolean | null
          jurisdiction_id: string
          known_code_host:
            | Database["public"]["Enums"]["known_code_host_enum"]
            | null
          known_code_host_id: string | null
          next_evidence_run_at: string | null
          overlapping_counties_fips: string[] | null
          permit_synthesis_fingerprint: string | null
          place_fips: string | null
          population: number | null
          priority_tier: Database["public"]["Enums"]["priority_tier_enum"]
          quarantine_reason: string | null
          quarantined_at: string | null
          search_aliases: string[] | null
          search_name: string | null
          state_fips: string
          state_name: string
        }
        Insert: {
          area_name: string
          county_fips: string
          cousub_fips?: string | null
          discovery_hints?: string[] | null
          endonym_confidence?:
            | Database["public"]["Enums"]["endonym_confidence_enum"]
            | null
          geometry_updated_at?: string | null
          geometry_uri?: string | null
          government_type: Database["public"]["Enums"]["government_type_enum"]
          has_zoning_ordinance?: boolean | null
          is_incorporated?: boolean | null
          jurisdiction_id: string
          known_code_host?:
            | Database["public"]["Enums"]["known_code_host_enum"]
            | null
          known_code_host_id?: string | null
          next_evidence_run_at?: string | null
          overlapping_counties_fips?: string[] | null
          permit_synthesis_fingerprint?: string | null
          place_fips?: string | null
          population?: number | null
          priority_tier?: Database["public"]["Enums"]["priority_tier_enum"]
          quarantine_reason?: string | null
          quarantined_at?: string | null
          search_aliases?: string[] | null
          search_name?: string | null
          state_fips: string
          state_name: string
        }
        Update: {
          area_name?: string
          county_fips?: string
          cousub_fips?: string | null
          discovery_hints?: string[] | null
          endonym_confidence?:
            | Database["public"]["Enums"]["endonym_confidence_enum"]
            | null
          geometry_updated_at?: string | null
          geometry_uri?: string | null
          government_type?: Database["public"]["Enums"]["government_type_enum"]
          has_zoning_ordinance?: boolean | null
          is_incorporated?: boolean | null
          jurisdiction_id?: string
          known_code_host?:
            | Database["public"]["Enums"]["known_code_host_enum"]
            | null
          known_code_host_id?: string | null
          next_evidence_run_at?: string | null
          overlapping_counties_fips?: string[] | null
          permit_synthesis_fingerprint?: string | null
          place_fips?: string | null
          population?: number | null
          priority_tier?: Database["public"]["Enums"]["priority_tier_enum"]
          quarantine_reason?: string | null
          quarantined_at?: string | null
          search_aliases?: string[] | null
          search_name?: string | null
          state_fips?: string
          state_name?: string
        }
        Relationships: []
      }
      research_ahj_discovery_batch: {
        Row: {
          chunk_index: number
          completed_at: string | null
          cycle_id: string
          error_file_id: string | null
          id: string
          input_file_id: string
          openai_batch_id: string
          output_file_id: string | null
          request_count: number
          round: number
          status: Database["public"]["Enums"]["batch_status_enum"]
          submitted_at: string
        }
        Insert: {
          chunk_index: number
          completed_at?: string | null
          cycle_id: string
          error_file_id?: string | null
          id?: string
          input_file_id: string
          openai_batch_id: string
          output_file_id?: string | null
          request_count: number
          round: number
          status?: Database["public"]["Enums"]["batch_status_enum"]
          submitted_at?: string
        }
        Update: {
          chunk_index?: number
          completed_at?: string | null
          cycle_id?: string
          error_file_id?: string | null
          id?: string
          input_file_id?: string
          openai_batch_id?: string
          output_file_id?: string | null
          request_count?: number
          round?: number
          status?: Database["public"]["Enums"]["batch_status_enum"]
          submitted_at?: string
        }
        Relationships: []
      }
      research_ahj_evidence_artifact: {
        Row: {
          agency: string | null
          area_name: string | null
          capture_integrity:
            | Database["public"]["Enums"]["capture_integrity_enum"]
            | null
          capture_kind: Database["public"]["Enums"]["capture_kind_enum"] | null
          capture_notes: string | null
          capture_status: Database["public"]["Enums"]["capture_status_enum"]
          capture_tool: Database["public"]["Enums"]["capture_tool_enum"] | null
          captured_bytes: number | null
          captured_mime: string | null
          category: Database["public"]["Enums"]["tech_category_enum"] | null
          content_relevance: number | null
          content_sha256: string | null
          discovered_at: string | null
          discovered_via: string | null
          doc_role: Database["public"]["Enums"]["doc_role_enum"] | null
          doc_type: Database["public"]["Enums"]["doc_type_enum"] | null
          duplicate_of: string | null
          evidence_confidence:
            | Database["public"]["Enums"]["evidence_confidence_enum"]
            | null
          fetched_at: string | null
          final_url: string | null
          gcs_bucket: string | null
          gcs_object_path: string | null
          government_type:
            | Database["public"]["Enums"]["government_type_enum"]
            | null
          http_status: number | null
          id: string
          is_zoning_map: boolean
          jurisdiction_id: string
          jurisdiction_match: Database["public"]["Enums"]["jurisdiction_match_enum"]
          last_validated_at: string | null
          liveness: Database["public"]["Enums"]["liveness_enum"] | null
          rubric_version: string
          run_id: string
          source_authority:
            | Database["public"]["Enums"]["source_authority_enum"]
            | null
          source_domain: string | null
          source_host_kind:
            | Database["public"]["Enums"]["source_host_kind_enum"]
            | null
          source_url: string
          staleness: Database["public"]["Enums"]["staleness_enum"] | null
          state_name: string | null
          supersedes_evidence_id: string | null
          title: string | null
          topic: Database["public"]["Enums"]["topic_enum"]
          version_token: string | null
          zoning_map_format:
            | Database["public"]["Enums"]["zoning_map_format_enum"]
            | null
        }
        Insert: {
          agency?: string | null
          area_name?: string | null
          capture_integrity?:
            | Database["public"]["Enums"]["capture_integrity_enum"]
            | null
          capture_kind?: Database["public"]["Enums"]["capture_kind_enum"] | null
          capture_notes?: string | null
          capture_status?: Database["public"]["Enums"]["capture_status_enum"]
          capture_tool?: Database["public"]["Enums"]["capture_tool_enum"] | null
          captured_bytes?: number | null
          captured_mime?: string | null
          category?: Database["public"]["Enums"]["tech_category_enum"] | null
          content_relevance?: number | null
          content_sha256?: string | null
          discovered_at?: string | null
          discovered_via?: string | null
          doc_role?: Database["public"]["Enums"]["doc_role_enum"] | null
          doc_type?: Database["public"]["Enums"]["doc_type_enum"] | null
          duplicate_of?: string | null
          evidence_confidence?:
            | Database["public"]["Enums"]["evidence_confidence_enum"]
            | null
          fetched_at?: string | null
          final_url?: string | null
          gcs_bucket?: string | null
          gcs_object_path?: string | null
          government_type?:
            | Database["public"]["Enums"]["government_type_enum"]
            | null
          http_status?: number | null
          id?: string
          is_zoning_map?: boolean
          jurisdiction_id: string
          jurisdiction_match?: Database["public"]["Enums"]["jurisdiction_match_enum"]
          last_validated_at?: string | null
          liveness?: Database["public"]["Enums"]["liveness_enum"] | null
          rubric_version?: string
          run_id: string
          source_authority?:
            | Database["public"]["Enums"]["source_authority_enum"]
            | null
          source_domain?: string | null
          source_host_kind?:
            | Database["public"]["Enums"]["source_host_kind_enum"]
            | null
          source_url: string
          staleness?: Database["public"]["Enums"]["staleness_enum"] | null
          state_name?: string | null
          supersedes_evidence_id?: string | null
          title?: string | null
          topic: Database["public"]["Enums"]["topic_enum"]
          version_token?: string | null
          zoning_map_format?:
            | Database["public"]["Enums"]["zoning_map_format_enum"]
            | null
        }
        Update: {
          agency?: string | null
          area_name?: string | null
          capture_integrity?:
            | Database["public"]["Enums"]["capture_integrity_enum"]
            | null
          capture_kind?: Database["public"]["Enums"]["capture_kind_enum"] | null
          capture_notes?: string | null
          capture_status?: Database["public"]["Enums"]["capture_status_enum"]
          capture_tool?: Database["public"]["Enums"]["capture_tool_enum"] | null
          captured_bytes?: number | null
          captured_mime?: string | null
          category?: Database["public"]["Enums"]["tech_category_enum"] | null
          content_relevance?: number | null
          content_sha256?: string | null
          discovered_at?: string | null
          discovered_via?: string | null
          doc_role?: Database["public"]["Enums"]["doc_role_enum"] | null
          doc_type?: Database["public"]["Enums"]["doc_type_enum"] | null
          duplicate_of?: string | null
          evidence_confidence?:
            | Database["public"]["Enums"]["evidence_confidence_enum"]
            | null
          fetched_at?: string | null
          final_url?: string | null
          gcs_bucket?: string | null
          gcs_object_path?: string | null
          government_type?:
            | Database["public"]["Enums"]["government_type_enum"]
            | null
          http_status?: number | null
          id?: string
          is_zoning_map?: boolean
          jurisdiction_id?: string
          jurisdiction_match?: Database["public"]["Enums"]["jurisdiction_match_enum"]
          last_validated_at?: string | null
          liveness?: Database["public"]["Enums"]["liveness_enum"] | null
          rubric_version?: string
          run_id?: string
          source_authority?:
            | Database["public"]["Enums"]["source_authority_enum"]
            | null
          source_domain?: string | null
          source_host_kind?:
            | Database["public"]["Enums"]["source_host_kind_enum"]
            | null
          source_url?: string
          staleness?: Database["public"]["Enums"]["staleness_enum"] | null
          state_name?: string | null
          supersedes_evidence_id?: string | null
          title?: string | null
          topic?: Database["public"]["Enums"]["topic_enum"]
          version_token?: string | null
          zoning_map_format?:
            | Database["public"]["Enums"]["zoning_map_format_enum"]
            | null
        }
        Relationships: [
          {
            foreignKeyName: "research_ahj_evidence_artifact_duplicate_of_fkey"
            columns: ["duplicate_of"]
            isOneToOne: false
            referencedRelation: "research_ahj_evidence_artifact"
            referencedColumns: ["id"]
          },
          {
            foreignKeyName: "research_ahj_evidence_artifact_jurisdiction_id_fkey"
            columns: ["jurisdiction_id"]
            isOneToOne: false
            referencedRelation: "research_ahj"
            referencedColumns: ["jurisdiction_id"]
          },
          {
            foreignKeyName: "research_ahj_evidence_artifact_jurisdiction_id_fkey"
            columns: ["jurisdiction_id"]
            isOneToOne: false
            referencedRelation: "research_ahj_topic_status_public"
            referencedColumns: ["jurisdiction_id"]
          },
          {
            foreignKeyName: "research_ahj_evidence_artifact_run_id_fkey"
            columns: ["run_id"]
            isOneToOne: false
            referencedRelation: "research_ahj_run"
            referencedColumns: ["id"]
          },
          {
            foreignKeyName: "research_ahj_evidence_artifact_supersedes_evidence_id_fkey"
            columns: ["supersedes_evidence_id"]
            isOneToOne: false
            referencedRelation: "research_ahj_evidence_artifact"
            referencedColumns: ["id"]
          },
        ]
      }
      research_ahj_evidence_object_retirement: {
        Row: {
          artifact_id: string | null
          companion_generation: number | null
          flagged_at: string
          gcs_bucket: string
          gcs_object_path: string
          object_generation: number | null
          reason: string
          retired_at: string | null
        }
        Insert: {
          artifact_id?: string | null
          companion_generation?: number | null
          flagged_at?: string
          gcs_bucket: string
          gcs_object_path: string
          object_generation?: number | null
          reason: string
          retired_at?: string | null
        }
        Update: {
          artifact_id?: string | null
          companion_generation?: number | null
          flagged_at?: string
          gcs_bucket?: string
          gcs_object_path?: string
          object_generation?: number | null
          reason?: string
          retired_at?: string | null
        }
        Relationships: []
      }
      research_ahj_evidence_reattribution: {
        Row: {
          artifact_id: string
          changed_at: string
          previous_companion_generation: number | null
          previous_duplicate_of: string | null
          previous_evidence_confidence:
            | Database["public"]["Enums"]["evidence_confidence_enum"]
            | null
          previous_gcs_bucket: string | null
          previous_gcs_object_path: string | null
          previous_jurisdiction_match:
            | Database["public"]["Enums"]["jurisdiction_match_enum"]
            | null
          previous_object_generation: number | null
          reason: string
        }
        Insert: {
          artifact_id: string
          changed_at?: string
          previous_companion_generation?: number | null
          previous_duplicate_of?: string | null
          previous_evidence_confidence?:
            | Database["public"]["Enums"]["evidence_confidence_enum"]
            | null
          previous_gcs_bucket?: string | null
          previous_gcs_object_path?: string | null
          previous_jurisdiction_match?:
            | Database["public"]["Enums"]["jurisdiction_match_enum"]
            | null
          previous_object_generation?: number | null
          reason: string
        }
        Update: {
          artifact_id?: string
          changed_at?: string
          previous_companion_generation?: number | null
          previous_duplicate_of?: string | null
          previous_evidence_confidence?:
            | Database["public"]["Enums"]["evidence_confidence_enum"]
            | null
          previous_gcs_bucket?: string | null
          previous_gcs_object_path?: string | null
          previous_jurisdiction_match?:
            | Database["public"]["Enums"]["jurisdiction_match_enum"]
            | null
          previous_object_generation?: number | null
          reason?: string
        }
        Relationships: [
          {
            foreignKeyName: "research_ahj_evidence_reattribution_artifact_id_fkey"
            columns: ["artifact_id"]
            isOneToOne: false
            referencedRelation: "research_ahj_evidence_artifact"
            referencedColumns: ["id"]
          },
        ]
      }
      research_ahj_permit_matrix: {
        Row: {
          area_name: string | null
          citations: Json | null
          conditional_use: string | null
          government_type:
            | Database["public"]["Enums"]["government_type_enum"]
            | null
          height_limit: string | null
          id: string
          jurisdiction_id: string
          last_synthesized_at: string | null
          lot_coverage: string | null
          project_type: Database["public"]["Enums"]["project_type_enum"]
          run_id: string
          setbacks: Json | null
          state_name: string | null
          synthesis_confidence:
            | Database["public"]["Enums"]["evidence_confidence_enum"]
            | null
          synthesis_model: string | null
          synthesis_notes: string | null
          tech_category: Database["public"]["Enums"]["tech_category_enum"]
          typical_fee: string | null
          typical_timeline: string | null
          zone_districts_allowed: string[] | null
        }
        Insert: {
          area_name?: string | null
          citations?: Json | null
          conditional_use?: string | null
          government_type?:
            | Database["public"]["Enums"]["government_type_enum"]
            | null
          height_limit?: string | null
          id?: string
          jurisdiction_id: string
          last_synthesized_at?: string | null
          lot_coverage?: string | null
          project_type: Database["public"]["Enums"]["project_type_enum"]
          run_id: string
          setbacks?: Json | null
          state_name?: string | null
          synthesis_confidence?:
            | Database["public"]["Enums"]["evidence_confidence_enum"]
            | null
          synthesis_model?: string | null
          synthesis_notes?: string | null
          tech_category: Database["public"]["Enums"]["tech_category_enum"]
          typical_fee?: string | null
          typical_timeline?: string | null
          zone_districts_allowed?: string[] | null
        }
        Update: {
          area_name?: string | null
          citations?: Json | null
          conditional_use?: string | null
          government_type?:
            | Database["public"]["Enums"]["government_type_enum"]
            | null
          height_limit?: string | null
          id?: string
          jurisdiction_id?: string
          last_synthesized_at?: string | null
          lot_coverage?: string | null
          project_type?: Database["public"]["Enums"]["project_type_enum"]
          run_id?: string
          setbacks?: Json | null
          state_name?: string | null
          synthesis_confidence?:
            | Database["public"]["Enums"]["evidence_confidence_enum"]
            | null
          synthesis_model?: string | null
          synthesis_notes?: string | null
          tech_category?: Database["public"]["Enums"]["tech_category_enum"]
          typical_fee?: string | null
          typical_timeline?: string | null
          zone_districts_allowed?: string[] | null
        }
        Relationships: [
          {
            foreignKeyName: "research_ahj_permit_matrix_jurisdiction_id_fkey"
            columns: ["jurisdiction_id"]
            isOneToOne: false
            referencedRelation: "research_ahj"
            referencedColumns: ["jurisdiction_id"]
          },
          {
            foreignKeyName: "research_ahj_permit_matrix_jurisdiction_id_fkey"
            columns: ["jurisdiction_id"]
            isOneToOne: false
            referencedRelation: "research_ahj_topic_status_public"
            referencedColumns: ["jurisdiction_id"]
          },
          {
            foreignKeyName: "research_ahj_permit_matrix_run_id_fkey"
            columns: ["run_id"]
            isOneToOne: false
            referencedRelation: "research_ahj_run"
            referencedColumns: ["id"]
          },
        ]
      }
      research_ahj_permit_matrix_suppression: {
        Row: {
          created_at: string
          created_by: string | null
          id: string
          jurisdiction_id: string
          project_type: Database["public"]["Enums"]["project_type_enum"] | null
          reason: string
          tech_category:
            | Database["public"]["Enums"]["tech_category_enum"]
            | null
        }
        Insert: {
          created_at?: string
          created_by?: string | null
          id?: string
          jurisdiction_id: string
          project_type?: Database["public"]["Enums"]["project_type_enum"] | null
          reason: string
          tech_category?:
            | Database["public"]["Enums"]["tech_category_enum"]
            | null
        }
        Update: {
          created_at?: string
          created_by?: string | null
          id?: string
          jurisdiction_id?: string
          project_type?: Database["public"]["Enums"]["project_type_enum"] | null
          reason?: string
          tech_category?:
            | Database["public"]["Enums"]["tech_category_enum"]
            | null
        }
        Relationships: [
          {
            foreignKeyName: "research_ahj_permit_matrix_suppression_jurisdiction_id_fkey"
            columns: ["jurisdiction_id"]
            isOneToOne: false
            referencedRelation: "research_ahj"
            referencedColumns: ["jurisdiction_id"]
          },
          {
            foreignKeyName: "research_ahj_permit_matrix_suppression_jurisdiction_id_fkey"
            columns: ["jurisdiction_id"]
            isOneToOne: false
            referencedRelation: "research_ahj_topic_status_public"
            referencedColumns: ["jurisdiction_id"]
          },
        ]
      }
      research_ahj_recent_activity: {
        Row: {
          citations: Json | null
          id: string
          incentives: Json | null
          incentives_synthesis_date: string | null
          jurisdiction_id: string
          last_synthesized_at: string
          moratorium: Json | null
          moratorium_synthesis_date: string | null
          project_type: Database["public"]["Enums"]["project_type_enum"] | null
          recent_ahj_activity: Json | null
          recent_ahj_activity_synthesis_date: string | null
          run_id: string
          tech_category:
            | Database["public"]["Enums"]["tech_category_enum"]
            | null
        }
        Insert: {
          citations?: Json | null
          id?: string
          incentives?: Json | null
          incentives_synthesis_date?: string | null
          jurisdiction_id: string
          last_synthesized_at?: string
          moratorium?: Json | null
          moratorium_synthesis_date?: string | null
          project_type?: Database["public"]["Enums"]["project_type_enum"] | null
          recent_ahj_activity?: Json | null
          recent_ahj_activity_synthesis_date?: string | null
          run_id: string
          tech_category?:
            | Database["public"]["Enums"]["tech_category_enum"]
            | null
        }
        Update: {
          citations?: Json | null
          id?: string
          incentives?: Json | null
          incentives_synthesis_date?: string | null
          jurisdiction_id?: string
          last_synthesized_at?: string
          moratorium?: Json | null
          moratorium_synthesis_date?: string | null
          project_type?: Database["public"]["Enums"]["project_type_enum"] | null
          recent_ahj_activity?: Json | null
          recent_ahj_activity_synthesis_date?: string | null
          run_id?: string
          tech_category?:
            | Database["public"]["Enums"]["tech_category_enum"]
            | null
        }
        Relationships: [
          {
            foreignKeyName: "research_ahj_recent_activity_jurisdiction_id_fkey"
            columns: ["jurisdiction_id"]
            isOneToOne: false
            referencedRelation: "research_ahj"
            referencedColumns: ["jurisdiction_id"]
          },
          {
            foreignKeyName: "research_ahj_recent_activity_jurisdiction_id_fkey"
            columns: ["jurisdiction_id"]
            isOneToOne: false
            referencedRelation: "research_ahj_topic_status_public"
            referencedColumns: ["jurisdiction_id"]
          },
          {
            foreignKeyName: "research_ahj_recent_activity_run_id_fkey"
            columns: ["run_id"]
            isOneToOne: false
            referencedRelation: "research_ahj_run"
            referencedColumns: ["id"]
          },
        ]
      }
      research_ahj_run: {
        Row: {
          budget_spent_usd: number
          completed_at: string | null
          cost_cap_hit: boolean
          discover_errors: number
          failure_reason: string | null
          force: boolean
          id: string
          jurisdiction_id: string
          reset: boolean
          retry_of: string | null
          started_at: string
          status: Database["public"]["Enums"]["run_status_enum"]
          trigger_kind: Database["public"]["Enums"]["trigger_kind_enum"]
        }
        Insert: {
          budget_spent_usd?: number
          completed_at?: string | null
          cost_cap_hit?: boolean
          discover_errors?: number
          failure_reason?: string | null
          force?: boolean
          id: string
          jurisdiction_id: string
          reset?: boolean
          retry_of?: string | null
          started_at?: string
          status?: Database["public"]["Enums"]["run_status_enum"]
          trigger_kind: Database["public"]["Enums"]["trigger_kind_enum"]
        }
        Update: {
          budget_spent_usd?: number
          completed_at?: string | null
          cost_cap_hit?: boolean
          discover_errors?: number
          failure_reason?: string | null
          force?: boolean
          id?: string
          jurisdiction_id?: string
          reset?: boolean
          retry_of?: string | null
          started_at?: string
          status?: Database["public"]["Enums"]["run_status_enum"]
          trigger_kind?: Database["public"]["Enums"]["trigger_kind_enum"]
        }
        Relationships: [
          {
            foreignKeyName: "research_ahj_run_jurisdiction_id_fkey"
            columns: ["jurisdiction_id"]
            isOneToOne: false
            referencedRelation: "research_ahj"
            referencedColumns: ["jurisdiction_id"]
          },
          {
            foreignKeyName: "research_ahj_run_jurisdiction_id_fkey"
            columns: ["jurisdiction_id"]
            isOneToOne: false
            referencedRelation: "research_ahj_topic_status_public"
            referencedColumns: ["jurisdiction_id"]
          },
          {
            foreignKeyName: "research_ahj_run_retry_of_fkey"
            columns: ["retry_of"]
            isOneToOne: false
            referencedRelation: "research_ahj_run"
            referencedColumns: ["id"]
          },
        ]
      }
      research_ahj_run_quality: {
        Row: {
          completion_tokens: number | null
          cost_usd: number | null
          generated_at: string | null
          id: string
          jurisdiction_id: string | null
          lane: Database["public"]["Enums"]["lane_enum"]
          latency_ms: number | null
          metrics: Json | null
          notes: string | null
          prompt_tokens: number | null
          quality_score: number | null
          run_id: string
          skill_name: string | null
          status: string | null
        }
        Insert: {
          completion_tokens?: number | null
          cost_usd?: number | null
          generated_at?: string | null
          id?: string
          jurisdiction_id?: string | null
          lane: Database["public"]["Enums"]["lane_enum"]
          latency_ms?: number | null
          metrics?: Json | null
          notes?: string | null
          prompt_tokens?: number | null
          quality_score?: number | null
          run_id: string
          skill_name?: string | null
          status?: string | null
        }
        Update: {
          completion_tokens?: number | null
          cost_usd?: number | null
          generated_at?: string | null
          id?: string
          jurisdiction_id?: string | null
          lane?: Database["public"]["Enums"]["lane_enum"]
          latency_ms?: number | null
          metrics?: Json | null
          notes?: string | null
          prompt_tokens?: number | null
          quality_score?: number | null
          run_id?: string
          skill_name?: string | null
          status?: string | null
        }
        Relationships: [
          {
            foreignKeyName: "research_ahj_run_quality_jurisdiction_id_fkey"
            columns: ["jurisdiction_id"]
            isOneToOne: false
            referencedRelation: "research_ahj"
            referencedColumns: ["jurisdiction_id"]
          },
          {
            foreignKeyName: "research_ahj_run_quality_jurisdiction_id_fkey"
            columns: ["jurisdiction_id"]
            isOneToOne: false
            referencedRelation: "research_ahj_topic_status_public"
            referencedColumns: ["jurisdiction_id"]
          },
          {
            foreignKeyName: "research_ahj_run_quality_run_id_fkey"
            columns: ["run_id"]
            isOneToOne: false
            referencedRelation: "research_ahj_run"
            referencedColumns: ["id"]
          },
        ]
      }
      research_ahj_run_topic: {
        Row: {
          artifacts_captured: number
          artifacts_discovered: number
          completed_at: string | null
          cost_usd: number
          discovery_feedback: Json | null
          eligible_after: string | null
          error: string | null
          escalated: boolean
          independent_source_count: number
          model_used: Database["public"]["Enums"]["discovery_model_enum"] | null
          notes: Json | null
          run_id: string
          started_at: string
          status: Database["public"]["Enums"]["topic_run_status_enum"]
          sufficiency: Database["public"]["Enums"]["sufficiency_enum"] | null
          synthesis_cost: number | null
          synthesis_model: string | null
          tiers_called: string[] | null
          topic: Database["public"]["Enums"]["topic_enum"]
        }
        Insert: {
          artifacts_captured?: number
          artifacts_discovered?: number
          completed_at?: string | null
          cost_usd?: number
          discovery_feedback?: Json | null
          eligible_after?: string | null
          error?: string | null
          escalated?: boolean
          independent_source_count?: number
          model_used?:
            | Database["public"]["Enums"]["discovery_model_enum"]
            | null
          notes?: Json | null
          run_id: string
          started_at?: string
          status?: Database["public"]["Enums"]["topic_run_status_enum"]
          sufficiency?: Database["public"]["Enums"]["sufficiency_enum"] | null
          synthesis_cost?: number | null
          synthesis_model?: string | null
          tiers_called?: string[] | null
          topic: Database["public"]["Enums"]["topic_enum"]
        }
        Update: {
          artifacts_captured?: number
          artifacts_discovered?: number
          completed_at?: string | null
          cost_usd?: number
          discovery_feedback?: Json | null
          eligible_after?: string | null
          error?: string | null
          escalated?: boolean
          independent_source_count?: number
          model_used?:
            | Database["public"]["Enums"]["discovery_model_enum"]
            | null
          notes?: Json | null
          run_id?: string
          started_at?: string
          status?: Database["public"]["Enums"]["topic_run_status_enum"]
          sufficiency?: Database["public"]["Enums"]["sufficiency_enum"] | null
          synthesis_cost?: number | null
          synthesis_model?: string | null
          tiers_called?: string[] | null
          topic?: Database["public"]["Enums"]["topic_enum"]
        }
        Relationships: [
          {
            foreignKeyName: "research_ahj_run_topic_run_id_fkey"
            columns: ["run_id"]
            isOneToOne: false
            referencedRelation: "research_ahj_run"
            referencedColumns: ["id"]
          },
        ]
      }
      research_ahj_synthesis_batch: {
        Row: {
          chunk_index: number
          completed_at: string | null
          cycle_id: string
          error_file_id: string | null
          id: string
          input_file_id: string
          openai_batch_id: string
          output_file_id: string | null
          request_count: number
          status: Database["public"]["Enums"]["batch_status_enum"]
          submitted_at: string
        }
        Insert: {
          chunk_index: number
          completed_at?: string | null
          cycle_id: string
          error_file_id?: string | null
          id?: string
          input_file_id: string
          openai_batch_id: string
          output_file_id?: string | null
          request_count: number
          status?: Database["public"]["Enums"]["batch_status_enum"]
          submitted_at?: string
        }
        Update: {
          chunk_index?: number
          completed_at?: string | null
          cycle_id?: string
          error_file_id?: string | null
          id?: string
          input_file_id?: string
          openai_batch_id?: string
          output_file_id?: string | null
          request_count?: number
          status?: Database["public"]["Enums"]["batch_status_enum"]
          submitted_at?: string
        }
        Relationships: []
      }
      research_ahj_topic_authority_rule: {
        Row: {
          citation_url: string | null
          created_at: string
          created_by: string | null
          defers_to_level: Database["public"]["Enums"]["ahj_level_enum"] | null
          exceptions: string[]
          government_type: Database["public"]["Enums"]["government_type_enum"]
          id: string
          justification: string
          priority_tiers:
            | Database["public"]["Enums"]["priority_tier_enum"][]
            | null
          reason: Database["public"]["Enums"]["exclusion_reason_enum"]
          state_fips: string | null
          topic: Database["public"]["Enums"]["topic_enum"]
        }
        Insert: {
          citation_url?: string | null
          created_at?: string
          created_by?: string | null
          defers_to_level?: Database["public"]["Enums"]["ahj_level_enum"] | null
          exceptions?: string[]
          government_type: Database["public"]["Enums"]["government_type_enum"]
          id?: string
          justification: string
          priority_tiers?:
            | Database["public"]["Enums"]["priority_tier_enum"][]
            | null
          reason: Database["public"]["Enums"]["exclusion_reason_enum"]
          state_fips?: string | null
          topic: Database["public"]["Enums"]["topic_enum"]
        }
        Update: {
          citation_url?: string | null
          created_at?: string
          created_by?: string | null
          defers_to_level?: Database["public"]["Enums"]["ahj_level_enum"] | null
          exceptions?: string[]
          government_type?: Database["public"]["Enums"]["government_type_enum"]
          id?: string
          justification?: string
          priority_tiers?:
            | Database["public"]["Enums"]["priority_tier_enum"][]
            | null
          reason?: Database["public"]["Enums"]["exclusion_reason_enum"]
          state_fips?: string | null
          topic?: Database["public"]["Enums"]["topic_enum"]
        }
        Relationships: []
      }
      research_ahj_topic_exclusion: {
        Row: {
          citation_url: string | null
          created_at: string
          created_by: string | null
          defers_to_jurisdiction_id: string | null
          defers_to_level: Database["public"]["Enums"]["ahj_level_enum"] | null
          id: string
          jurisdiction_id: string
          justification: string
          reason: Database["public"]["Enums"]["exclusion_reason_enum"]
          topic: Database["public"]["Enums"]["topic_enum"] | null
        }
        Insert: {
          citation_url?: string | null
          created_at?: string
          created_by?: string | null
          defers_to_jurisdiction_id?: string | null
          defers_to_level?: Database["public"]["Enums"]["ahj_level_enum"] | null
          id?: string
          jurisdiction_id: string
          justification: string
          reason: Database["public"]["Enums"]["exclusion_reason_enum"]
          topic?: Database["public"]["Enums"]["topic_enum"] | null
        }
        Update: {
          citation_url?: string | null
          created_at?: string
          created_by?: string | null
          defers_to_jurisdiction_id?: string | null
          defers_to_level?: Database["public"]["Enums"]["ahj_level_enum"] | null
          id?: string
          jurisdiction_id?: string
          justification?: string
          reason?: Database["public"]["Enums"]["exclusion_reason_enum"]
          topic?: Database["public"]["Enums"]["topic_enum"] | null
        }
        Relationships: [
          {
            foreignKeyName: "research_ahj_topic_exclusion_defers_to_jurisdiction_id_fkey"
            columns: ["defers_to_jurisdiction_id"]
            isOneToOne: false
            referencedRelation: "research_ahj"
            referencedColumns: ["jurisdiction_id"]
          },
          {
            foreignKeyName: "research_ahj_topic_exclusion_defers_to_jurisdiction_id_fkey"
            columns: ["defers_to_jurisdiction_id"]
            isOneToOne: false
            referencedRelation: "research_ahj_topic_status_public"
            referencedColumns: ["jurisdiction_id"]
          },
          {
            foreignKeyName: "research_ahj_topic_exclusion_jurisdiction_id_fkey"
            columns: ["jurisdiction_id"]
            isOneToOne: false
            referencedRelation: "research_ahj"
            referencedColumns: ["jurisdiction_id"]
          },
          {
            foreignKeyName: "research_ahj_topic_exclusion_jurisdiction_id_fkey"
            columns: ["jurisdiction_id"]
            isOneToOne: false
            referencedRelation: "research_ahj_topic_status_public"
            referencedColumns: ["jurisdiction_id"]
          },
        ]
      }
      shared_map_views: {
        Row: {
          created_at: string
          created_by: string | null
          group_visibility: Json
          id: string
          layer_visibility: Json
          map_id: string
          polygon: Json
          workspace_id: string
        }
        Insert: {
          created_at?: string
          created_by?: string | null
          group_visibility?: Json
          id: string
          layer_visibility?: Json
          map_id: string
          polygon: Json
          workspace_id: string
        }
        Update: {
          created_at?: string
          created_by?: string | null
          group_visibility?: Json
          id?: string
          layer_visibility?: Json
          map_id?: string
          polygon?: Json
          workspace_id?: string
        }
        Relationships: [
          {
            foreignKeyName: "shared_map_views_created_by_fkey"
            columns: ["created_by"]
            isOneToOne: false
            referencedRelation: "user_profiles"
            referencedColumns: ["id"]
          },
          {
            foreignKeyName: "shared_map_views_map_id_fkey"
            columns: ["map_id"]
            isOneToOne: false
            referencedRelation: "maps"
            referencedColumns: ["id"]
          },
          {
            foreignKeyName: "shared_map_views_workspace_id_fkey"
            columns: ["workspace_id"]
            isOneToOne: false
            referencedRelation: "workspaces"
            referencedColumns: ["id"]
          },
        ]
      }
      site_diligence_criteria: {
        Row: {
          auto_evaluate: boolean
          basemap_style: string | null
          created_at: string
          created_by: string | null
          current_revision_id: string | null
          direction: string
          evaluation_contract: Json | null
          evaluation_graph: Json | null
          evaluation_graph_enabled: boolean
          id: string
          map_framing: string
          map_pdf_download_enabled: boolean
          methodology: string | null
          name: string
          parameters: Json | null
          published_at: string | null
          published_by: string | null
          published_revision_id: string | null
          published_source_revision_id: string | null
          report_template: Json | null
          scope: string
          scoring_rubric: Json | null
          search_radius_miles: number
          source_criterion_id: string | null
          summary_metric_instructions: string | null
          summary_metric_type: string
          thresholds: Json
          unit: string
          updated_at: string
          updated_by: string | null
          workspace_id: string | null
          zones: Json
        }
        Insert: {
          auto_evaluate?: boolean
          basemap_style?: string | null
          created_at?: string
          created_by?: string | null
          current_revision_id?: string | null
          direction: string
          evaluation_contract?: Json | null
          evaluation_graph?: Json | null
          evaluation_graph_enabled?: boolean
          id?: string
          map_framing?: string
          map_pdf_download_enabled?: boolean
          methodology?: string | null
          name: string
          parameters?: Json | null
          published_at?: string | null
          published_by?: string | null
          published_revision_id?: string | null
          published_source_revision_id?: string | null
          report_template?: Json | null
          scope?: string
          scoring_rubric?: Json | null
          search_radius_miles?: number
          source_criterion_id?: string | null
          summary_metric_instructions?: string | null
          summary_metric_type?: string
          thresholds: Json
          unit: string
          updated_at?: string
          updated_by?: string | null
          workspace_id?: string | null
          zones: Json
        }
        Update: {
          auto_evaluate?: boolean
          basemap_style?: string | null
          created_at?: string
          created_by?: string | null
          current_revision_id?: string | null
          direction?: string
          evaluation_contract?: Json | null
          evaluation_graph?: Json | null
          evaluation_graph_enabled?: boolean
          id?: string
          map_framing?: string
          map_pdf_download_enabled?: boolean
          methodology?: string | null
          name?: string
          parameters?: Json | null
          published_at?: string | null
          published_by?: string | null
          published_revision_id?: string | null
          published_source_revision_id?: string | null
          report_template?: Json | null
          scope?: string
          scoring_rubric?: Json | null
          search_radius_miles?: number
          source_criterion_id?: string | null
          summary_metric_instructions?: string | null
          summary_metric_type?: string
          thresholds?: Json
          unit?: string
          updated_at?: string
          updated_by?: string | null
          workspace_id?: string | null
          zones?: Json
        }
        Relationships: [
          {
            foreignKeyName: "site_diligence_criteria_current_revision_id_fkey"
            columns: ["current_revision_id"]
            isOneToOne: false
            referencedRelation: "site_diligence_criteria_revisions"
            referencedColumns: ["id"]
          },
          {
            foreignKeyName: "site_diligence_criteria_published_revision_id_fkey"
            columns: ["published_revision_id"]
            isOneToOne: false
            referencedRelation: "site_diligence_criteria_revisions"
            referencedColumns: ["id"]
          },
          {
            foreignKeyName: "site_diligence_criteria_published_source_revision_id_fkey"
            columns: ["published_source_revision_id"]
            isOneToOne: false
            referencedRelation: "site_diligence_criteria_revisions"
            referencedColumns: ["id"]
          },
          {
            foreignKeyName: "site_diligence_criteria_source_criterion_id_fkey"
            columns: ["source_criterion_id"]
            isOneToOne: false
            referencedRelation: "site_diligence_criteria"
            referencedColumns: ["id"]
          },
          {
            foreignKeyName: "site_diligence_criteria_workspace_id_fkey"
            columns: ["workspace_id"]
            isOneToOne: false
            referencedRelation: "workspaces"
            referencedColumns: ["id"]
          },
        ]
      }
      site_diligence_criteria_results: {
        Row: {
          analysis_layer_id: string | null
          created_at: string
          created_by: string | null
          criterion_id: string
          criterion_map_id: string | null
          criterion_revision_id: string | null
          criterion_synthesis: string | null
          dataset_manifest: Json | null
          evaluated_at: string | null
          evaluation_contract: Json | null
          html_report_url: string | null
          id: string
          is_stale: boolean
          layer_row_id: number
          outcome: string | null
          report_scroll_target: string | null
          review_findings: Json | null
          search_radius_miles: number | null
          status: string
          summary_metric_text: string | null
          summary_metric_value: number | null
          thread_id: string | null
          updated_at: string
          updated_by: string | null
          zone_index: number | null
        }
        Insert: {
          analysis_layer_id?: string | null
          created_at?: string
          created_by?: string | null
          criterion_id: string
          criterion_map_id?: string | null
          criterion_revision_id?: string | null
          criterion_synthesis?: string | null
          dataset_manifest?: Json | null
          evaluated_at?: string | null
          evaluation_contract?: Json | null
          html_report_url?: string | null
          id?: string
          is_stale?: boolean
          layer_row_id: number
          outcome?: string | null
          report_scroll_target?: string | null
          review_findings?: Json | null
          search_radius_miles?: number | null
          status?: string
          summary_metric_text?: string | null
          summary_metric_value?: number | null
          thread_id?: string | null
          updated_at?: string
          updated_by?: string | null
          zone_index?: number | null
        }
        Update: {
          analysis_layer_id?: string | null
          created_at?: string
          created_by?: string | null
          criterion_id?: string
          criterion_map_id?: string | null
          criterion_revision_id?: string | null
          criterion_synthesis?: string | null
          dataset_manifest?: Json | null
          evaluated_at?: string | null
          evaluation_contract?: Json | null
          html_report_url?: string | null
          id?: string
          is_stale?: boolean
          layer_row_id?: number
          outcome?: string | null
          report_scroll_target?: string | null
          review_findings?: Json | null
          search_radius_miles?: number | null
          status?: string
          summary_metric_text?: string | null
          summary_metric_value?: number | null
          thread_id?: string | null
          updated_at?: string
          updated_by?: string | null
          zone_index?: number | null
        }
        Relationships: [
          {
            foreignKeyName: "site_diligence_criteria_results_analysis_layer_id_fkey"
            columns: ["analysis_layer_id"]
            isOneToOne: false
            referencedRelation: "layers"
            referencedColumns: ["id"]
          },
          {
            foreignKeyName: "site_diligence_criteria_results_criterion_id_fkey"
            columns: ["criterion_id"]
            isOneToOne: false
            referencedRelation: "site_diligence_criteria"
            referencedColumns: ["id"]
          },
          {
            foreignKeyName: "site_diligence_criteria_results_criterion_map_id_fkey"
            columns: ["criterion_map_id"]
            isOneToOne: false
            referencedRelation: "maps"
            referencedColumns: ["id"]
          },
          {
            foreignKeyName: "site_diligence_criteria_results_criterion_revision_id_fkey"
            columns: ["criterion_revision_id"]
            isOneToOne: false
            referencedRelation: "site_diligence_criteria_revisions"
            referencedColumns: ["id"]
          },
          {
            foreignKeyName: "site_diligence_criteria_results_layer_row_id_fkey"
            columns: ["layer_row_id"]
            isOneToOne: false
            referencedRelation: "layer_rows"
            referencedColumns: ["id"]
          },
          {
            foreignKeyName: "site_diligence_criteria_results_thread_id_fkey"
            columns: ["thread_id"]
            isOneToOne: false
            referencedRelation: "agent_threads"
            referencedColumns: ["id"]
          },
        ]
      }
      site_diligence_criteria_revisions: {
        Row: {
          created_at: string
          created_by: string | null
          criterion_id: string
          direction: string
          id: string
          methodology: string | null
          name: string
          parameters: Json | null
          revision_number: number
          scope: string
          scoring_rubric: Json | null
          search_radius_miles: number
          summary_metric_instructions: string | null
          thresholds: Json
          unit: string
          zones: Json
        }
        Insert: {
          created_at?: string
          created_by?: string | null
          criterion_id: string
          direction: string
          id?: string
          methodology?: string | null
          name: string
          parameters?: Json | null
          revision_number: number
          scope?: string
          scoring_rubric?: Json | null
          search_radius_miles?: number
          summary_metric_instructions?: string | null
          thresholds: Json
          unit: string
          zones: Json
        }
        Update: {
          created_at?: string
          created_by?: string | null
          criterion_id?: string
          direction?: string
          id?: string
          methodology?: string | null
          name?: string
          parameters?: Json | null
          revision_number?: number
          scope?: string
          scoring_rubric?: Json | null
          search_radius_miles?: number
          summary_metric_instructions?: string | null
          thresholds?: Json
          unit?: string
          zones?: Json
        }
        Relationships: [
          {
            foreignKeyName: "site_diligence_criteria_revisions_criterion_id_fkey"
            columns: ["criterion_id"]
            isOneToOne: false
            referencedRelation: "site_diligence_criteria"
            referencedColumns: ["id"]
          },
        ]
      }
      site_diligence_pipeline_criteria: {
        Row: {
          created_at: string
          created_by: string | null
          criterion_id: string
          pipeline_layer_id: string
          position: number | null
          retired_at: string | null
        }
        Insert: {
          created_at?: string
          created_by?: string | null
          criterion_id: string
          pipeline_layer_id: string
          position?: number | null
          retired_at?: string | null
        }
        Update: {
          created_at?: string
          created_by?: string | null
          criterion_id?: string
          pipeline_layer_id?: string
          position?: number | null
          retired_at?: string | null
        }
        Relationships: [
          {
            foreignKeyName: "site_diligence_pipeline_criteria_criterion_id_fkey"
            columns: ["criterion_id"]
            isOneToOne: false
            referencedRelation: "site_diligence_criteria"
            referencedColumns: ["id"]
          },
          {
            foreignKeyName: "site_diligence_pipeline_criteria_pipeline_layer_id_fkey"
            columns: ["pipeline_layer_id"]
            isOneToOne: false
            referencedRelation: "layers"
            referencedColumns: ["id"]
          },
        ]
      }
      site_notes: {
        Row: {
          content: Json
          created_at: string
          id: string
          layer_row_id: number
          user_id: string
          workspace_id: string
        }
        Insert: {
          content?: Json
          created_at?: string
          id?: string
          layer_row_id: number
          user_id: string
          workspace_id: string
        }
        Update: {
          content?: Json
          created_at?: string
          id?: string
          layer_row_id?: number
          user_id?: string
          workspace_id?: string
        }
        Relationships: [
          {
            foreignKeyName: "site_notes_layer_row_id_fkey"
            columns: ["layer_row_id"]
            isOneToOne: false
            referencedRelation: "layer_rows"
            referencedColumns: ["id"]
          },
          {
            foreignKeyName: "site_notes_workspace_id_fkey"
            columns: ["workspace_id"]
            isOneToOne: false
            referencedRelation: "workspaces"
            referencedColumns: ["id"]
          },
        ]
      }
      site_view_memberships: {
        Row: {
          layer_row_id: number
          site_view_id: string
          updated_at: string
          updated_by: string | null
        }
        Insert: {
          layer_row_id: number
          site_view_id: string
          updated_at?: string
          updated_by?: string | null
        }
        Update: {
          layer_row_id?: number
          site_view_id?: string
          updated_at?: string
          updated_by?: string | null
        }
        Relationships: [
          {
            foreignKeyName: "site_view_memberships_layer_row_id_fkey"
            columns: ["layer_row_id"]
            isOneToOne: true
            referencedRelation: "layer_rows"
            referencedColumns: ["id"]
          },
          {
            foreignKeyName: "site_view_memberships_site_view_id_fkey"
            columns: ["site_view_id"]
            isOneToOne: false
            referencedRelation: "site_views"
            referencedColumns: ["id"]
          },
        ]
      }
      site_views: {
        Row: {
          created_at: string
          created_by: string | null
          id: string
          is_default: boolean
          name: string
          position: number | null
          workspace_id: string
        }
        Insert: {
          created_at?: string
          created_by?: string | null
          id?: string
          is_default?: boolean
          name: string
          position?: number | null
          workspace_id: string
        }
        Update: {
          created_at?: string
          created_by?: string | null
          id?: string
          is_default?: boolean
          name?: string
          position?: number | null
          workspace_id?: string
        }
        Relationships: [
          {
            foreignKeyName: "site_views_workspace_id_fkey"
            columns: ["workspace_id"]
            isOneToOne: false
            referencedRelation: "workspaces"
            referencedColumns: ["id"]
          },
        ]
      }
      staff: {
        Row: {
          created_at: string
          note: string | null
          user_id: string
        }
        Insert: {
          created_at?: string
          note?: string | null
          user_id: string
        }
        Update: {
          created_at?: string
          note?: string | null
          user_id?: string
        }
        Relationships: []
      }
      user_profiles: {
        Row: {
          created_at: string | null
          display_name: string | null
          id: string
          updated_at: string | null
        }
        Insert: {
          created_at?: string | null
          display_name?: string | null
          id: string
          updated_at?: string | null
        }
        Update: {
          created_at?: string | null
          display_name?: string | null
          id?: string
          updated_at?: string | null
        }
        Relationships: []
      }
      workspace_api_keys: {
        Row: {
          created_at: string
          id: string
          key_hash: string
          key_preview: string
          name: string
          updated_at: string
          workspace_user_id: string
        }
        Insert: {
          created_at?: string
          id?: string
          key_hash: string
          key_preview: string
          name: string
          updated_at?: string
          workspace_user_id: string
        }
        Update: {
          created_at?: string
          id?: string
          key_hash?: string
          key_preview?: string
          name?: string
          updated_at?: string
          workspace_user_id?: string
        }
        Relationships: [
          {
            foreignKeyName: "workspace_api_keys_workspace_user_id_fkey"
            columns: ["workspace_user_id"]
            isOneToOne: false
            referencedRelation: "workspace_users"
            referencedColumns: ["id"]
          },
        ]
      }
      workspace_free_report_pipelines: {
        Row: {
          map_id: string | null
          pipeline_layer_id: string
          theme: string
          workspace_id: string
        }
        Insert: {
          map_id?: string | null
          pipeline_layer_id: string
          theme: string
          workspace_id: string
        }
        Update: {
          map_id?: string | null
          pipeline_layer_id?: string
          theme?: string
          workspace_id?: string
        }
        Relationships: [
          {
            foreignKeyName: "workspace_free_report_pipelines_map_id_fkey"
            columns: ["map_id"]
            isOneToOne: false
            referencedRelation: "maps"
            referencedColumns: ["id"]
          },
          {
            foreignKeyName: "workspace_free_report_pipelines_pipeline_layer_id_fkey"
            columns: ["pipeline_layer_id"]
            isOneToOne: false
            referencedRelation: "layers"
            referencedColumns: ["id"]
          },
          {
            foreignKeyName: "workspace_free_report_pipelines_theme_fkey"
            columns: ["theme"]
            isOneToOne: false
            referencedRelation: "free_report_theme_templates"
            referencedColumns: ["theme"]
          },
          {
            foreignKeyName: "workspace_free_report_pipelines_workspace_id_fkey"
            columns: ["workspace_id"]
            isOneToOne: false
            referencedRelation: "workspaces"
            referencedColumns: ["id"]
          },
        ]
      }
      workspace_invitations: {
        Row: {
          accepted_at: string | null
          accepted_by: string | null
          created_at: string
          declined_at: string | null
          declined_by: string | null
          email: string
          expires_at: string
          id: string
          invited_at: string
          invited_by: string
          role: Database["public"]["Enums"]["user_role"]
          status: Database["public"]["Enums"]["invitation_status"]
          token: string
          updated_at: string
          workspace_id: string
        }
        Insert: {
          accepted_at?: string | null
          accepted_by?: string | null
          created_at?: string
          declined_at?: string | null
          declined_by?: string | null
          email: string
          expires_at: string
          id?: string
          invited_at?: string
          invited_by: string
          role: Database["public"]["Enums"]["user_role"]
          status?: Database["public"]["Enums"]["invitation_status"]
          token: string
          updated_at?: string
          workspace_id: string
        }
        Update: {
          accepted_at?: string | null
          accepted_by?: string | null
          created_at?: string
          declined_at?: string | null
          declined_by?: string | null
          email?: string
          expires_at?: string
          id?: string
          invited_at?: string
          invited_by?: string
          role?: Database["public"]["Enums"]["user_role"]
          status?: Database["public"]["Enums"]["invitation_status"]
          token?: string
          updated_at?: string
          workspace_id?: string
        }
        Relationships: [
          {
            foreignKeyName: "workspace_invitations_workspace_id_fkey"
            columns: ["workspace_id"]
            isOneToOne: false
            referencedRelation: "workspaces"
            referencedColumns: ["id"]
          },
        ]
      }
      workspace_membership_events: {
        Row: {
          actor_id: string | null
          created_at: string
          event: string
          id: string
          previous_role: Database["public"]["Enums"]["user_role"] | null
          role: Database["public"]["Enums"]["user_role"] | null
          source: string
          user_email: string | null
          user_id: string
          workspace_id: string
        }
        Insert: {
          actor_id?: string | null
          created_at?: string
          event: string
          id?: string
          previous_role?: Database["public"]["Enums"]["user_role"] | null
          role?: Database["public"]["Enums"]["user_role"] | null
          source: string
          user_email?: string | null
          user_id: string
          workspace_id: string
        }
        Update: {
          actor_id?: string | null
          created_at?: string
          event?: string
          id?: string
          previous_role?: Database["public"]["Enums"]["user_role"] | null
          role?: Database["public"]["Enums"]["user_role"] | null
          source?: string
          user_email?: string | null
          user_id?: string
          workspace_id?: string
        }
        Relationships: [
          {
            foreignKeyName: "workspace_membership_events_workspace_id_fkey"
            columns: ["workspace_id"]
            isOneToOne: false
            referencedRelation: "workspaces"
            referencedColumns: ["id"]
          },
        ]
      }
      workspace_spend_caps: {
        Row: {
          alert_enabled: boolean
          cap_reached_alerted_at: string | null
          cap_usd: number
          counted_since: string
          counted_sources: string[]
          created_at: string
          created_by: string | null
          enabled: boolean
          low_balance_alerted_at: string | null
          spent_usd: number
          updated_at: string
          updated_by: string | null
          workspace_id: string
        }
        Insert: {
          alert_enabled?: boolean
          cap_reached_alerted_at?: string | null
          cap_usd: number
          counted_since?: string
          counted_sources?: string[]
          created_at?: string
          created_by?: string | null
          enabled?: boolean
          low_balance_alerted_at?: string | null
          spent_usd?: number
          updated_at?: string
          updated_by?: string | null
          workspace_id: string
        }
        Update: {
          alert_enabled?: boolean
          cap_reached_alerted_at?: string | null
          cap_usd?: number
          counted_since?: string
          counted_sources?: string[]
          created_at?: string
          created_by?: string | null
          enabled?: boolean
          low_balance_alerted_at?: string | null
          spent_usd?: number
          updated_at?: string
          updated_by?: string | null
          workspace_id?: string
        }
        Relationships: [
          {
            foreignKeyName: "workspace_spend_caps_workspace_id_fkey"
            columns: ["workspace_id"]
            isOneToOne: true
            referencedRelation: "workspaces"
            referencedColumns: ["id"]
          },
        ]
      }
      workspace_sso_bypass_emails: {
        Row: {
          connection_id: string
          email: string
        }
        Insert: {
          connection_id: string
          email: string
        }
        Update: {
          connection_id?: string
          email?: string
        }
        Relationships: [
          {
            foreignKeyName: "workspace_sso_bypass_emails_connection_id_fkey"
            columns: ["connection_id"]
            isOneToOne: false
            referencedRelation: "workspace_sso_connections"
            referencedColumns: ["id"]
          },
        ]
      }
      workspace_sso_connections: {
        Row: {
          created_at: string
          enforce_sso: boolean
          id: string
          jit_role: Database["public"]["Enums"]["user_role"]
          provider: string
          routing_enabled: boolean
          session_max_age: unknown
          slug: string
          updated_at: string
          workspace_id: string
        }
        Insert: {
          created_at?: string
          enforce_sso?: boolean
          id?: string
          jit_role?: Database["public"]["Enums"]["user_role"]
          provider: string
          routing_enabled?: boolean
          session_max_age?: unknown
          slug: string
          updated_at?: string
          workspace_id: string
        }
        Update: {
          created_at?: string
          enforce_sso?: boolean
          id?: string
          jit_role?: Database["public"]["Enums"]["user_role"]
          provider?: string
          routing_enabled?: boolean
          session_max_age?: unknown
          slug?: string
          updated_at?: string
          workspace_id?: string
        }
        Relationships: [
          {
            foreignKeyName: "workspace_sso_connections_workspace_id_fkey"
            columns: ["workspace_id"]
            isOneToOne: false
            referencedRelation: "workspaces"
            referencedColumns: ["id"]
          },
        ]
      }
      workspace_sso_domains: {
        Row: {
          connection_id: string
          domain: string
        }
        Insert: {
          connection_id: string
          domain: string
        }
        Update: {
          connection_id?: string
          domain?: string
        }
        Relationships: [
          {
            foreignKeyName: "workspace_sso_domains_connection_id_fkey"
            columns: ["connection_id"]
            isOneToOne: false
            referencedRelation: "workspace_sso_connections"
            referencedColumns: ["id"]
          },
        ]
      }
      workspace_users: {
        Row: {
          created_at: string
          id: string
          is_hidden: boolean
          role: Database["public"]["Enums"]["user_role"]
          updated_at: string
          user_id: string
          workspace_id: string
        }
        Insert: {
          created_at?: string
          id?: string
          is_hidden?: boolean
          role: Database["public"]["Enums"]["user_role"]
          updated_at?: string
          user_id: string
          workspace_id: string
        }
        Update: {
          created_at?: string
          id?: string
          is_hidden?: boolean
          role?: Database["public"]["Enums"]["user_role"]
          updated_at?: string
          user_id?: string
          workspace_id?: string
        }
        Relationships: [
          {
            foreignKeyName: "workspace_users_workspace_id_fkey"
            columns: ["workspace_id"]
            isOneToOne: false
            referencedRelation: "workspaces"
            referencedColumns: ["id"]
          },
        ]
      }
      workspaces: {
        Row: {
          created_at: string
          created_by: string | null
          features: Json
          id: string
          name: string
          slug: string
          tier: Database["public"]["Enums"]["workspace_tier"]
          updated_at: string
          updated_by: string | null
        }
        Insert: {
          created_at?: string
          created_by?: string | null
          features?: Json
          id?: string
          name: string
          slug?: string
          tier?: Database["public"]["Enums"]["workspace_tier"]
          updated_at?: string
          updated_by?: string | null
        }
        Update: {
          created_at?: string
          created_by?: string | null
          features?: Json
          id?: string
          name?: string
          slug?: string
          tier?: Database["public"]["Enums"]["workspace_tier"]
          updated_at?: string
          updated_by?: string | null
        }
        Relationships: []
      }
    }
    Views: {
      blocked_email_domains: {
        Row: {
          domain: string | null
        }
        Relationships: []
      }
      list_workspace_members: {
        Row: {
          display_name: string | null
          email: string | null
          expires_at: string | null
          id: string | null
          invitation_id: string | null
          role: Database["public"]["Enums"]["user_role"] | null
          status: Database["public"]["Enums"]["invitation_status"] | null
          workspace_id: string | null
        }
        Relationships: []
      }
      pending_invitations: {
        Row: {
          expires_at: string | null
          id: string | null
          invited_at: string | null
          invited_by: string | null
          invited_by_name: string | null
          role: Database["public"]["Enums"]["user_role"] | null
          token: string | null
          workspace_id: string | null
          workspace_name: string | null
          workspace_slug: string | null
        }
        Relationships: [
          {
            foreignKeyName: "workspace_invitations_workspace_id_fkey"
            columns: ["workspace_id"]
            isOneToOne: false
            referencedRelation: "workspaces"
            referencedColumns: ["id"]
          },
        ]
      }
      research_ahj_permit_matrix_public: {
        Row: {
          area_name: string | null
          citations: Json | null
          conditional_use: string | null
          government_type:
            | Database["public"]["Enums"]["government_type_enum"]
            | null
          height_limit: string | null
          jurisdiction_id: string | null
          last_synthesized_at: string | null
          lot_coverage: string | null
          project_type: Database["public"]["Enums"]["project_type_enum"] | null
          setbacks: Json | null
          state_name: string | null
          synthesis_confidence:
            | Database["public"]["Enums"]["evidence_confidence_enum"]
            | null
          tech_category:
            | Database["public"]["Enums"]["tech_category_enum"]
            | null
          typical_fee: string | null
          typical_timeline: string | null
          zone_districts_allowed: string[] | null
        }
        Insert: {
          area_name?: string | null
          citations?: Json | null
          conditional_use?: string | null
          government_type?:
            | Database["public"]["Enums"]["government_type_enum"]
            | null
          height_limit?: string | null
          jurisdiction_id?: string | null
          last_synthesized_at?: string | null
          lot_coverage?: string | null
          project_type?: Database["public"]["Enums"]["project_type_enum"] | null
          setbacks?: Json | null
          state_name?: string | null
          synthesis_confidence?:
            | Database["public"]["Enums"]["evidence_confidence_enum"]
            | null
          tech_category?:
            | Database["public"]["Enums"]["tech_category_enum"]
            | null
          typical_fee?: string | null
          typical_timeline?: string | null
          zone_districts_allowed?: string[] | null
        }
        Update: {
          area_name?: string | null
          citations?: Json | null
          conditional_use?: string | null
          government_type?:
            | Database["public"]["Enums"]["government_type_enum"]
            | null
          height_limit?: string | null
          jurisdiction_id?: string | null
          last_synthesized_at?: string | null
          lot_coverage?: string | null
          project_type?: Database["public"]["Enums"]["project_type_enum"] | null
          setbacks?: Json | null
          state_name?: string | null
          synthesis_confidence?:
            | Database["public"]["Enums"]["evidence_confidence_enum"]
            | null
          tech_category?:
            | Database["public"]["Enums"]["tech_category_enum"]
            | null
          typical_fee?: string | null
          typical_timeline?: string | null
          zone_districts_allowed?: string[] | null
        }
        Relationships: [
          {
            foreignKeyName: "research_ahj_permit_matrix_jurisdiction_id_fkey"
            columns: ["jurisdiction_id"]
            isOneToOne: false
            referencedRelation: "research_ahj"
            referencedColumns: ["jurisdiction_id"]
          },
          {
            foreignKeyName: "research_ahj_permit_matrix_jurisdiction_id_fkey"
            columns: ["jurisdiction_id"]
            isOneToOne: false
            referencedRelation: "research_ahj_topic_status_public"
            referencedColumns: ["jurisdiction_id"]
          },
        ]
      }
      research_ahj_recent_activity_public: {
        Row: {
          incentives: Json | null
          incentives_synthesis_date: string | null
          jurisdiction_id: string | null
          last_synthesized_at: string | null
          moratorium: Json | null
          moratorium_synthesis_date: string | null
          project_type: Database["public"]["Enums"]["project_type_enum"] | null
          recent_ahj_activity: Json | null
          recent_ahj_activity_synthesis_date: string | null
          tech_category:
            | Database["public"]["Enums"]["tech_category_enum"]
            | null
        }
        Insert: {
          incentives?: Json | null
          incentives_synthesis_date?: string | null
          jurisdiction_id?: string | null
          last_synthesized_at?: string | null
          moratorium?: Json | null
          moratorium_synthesis_date?: string | null
          project_type?: Database["public"]["Enums"]["project_type_enum"] | null
          recent_ahj_activity?: Json | null
          recent_ahj_activity_synthesis_date?: string | null
          tech_category?:
            | Database["public"]["Enums"]["tech_category_enum"]
            | null
        }
        Update: {
          incentives?: Json | null
          incentives_synthesis_date?: string | null
          jurisdiction_id?: string | null
          last_synthesized_at?: string | null
          moratorium?: Json | null
          moratorium_synthesis_date?: string | null
          project_type?: Database["public"]["Enums"]["project_type_enum"] | null
          recent_ahj_activity?: Json | null
          recent_ahj_activity_synthesis_date?: string | null
          tech_category?:
            | Database["public"]["Enums"]["tech_category_enum"]
            | null
        }
        Relationships: [
          {
            foreignKeyName: "research_ahj_recent_activity_jurisdiction_id_fkey"
            columns: ["jurisdiction_id"]
            isOneToOne: false
            referencedRelation: "research_ahj"
            referencedColumns: ["jurisdiction_id"]
          },
          {
            foreignKeyName: "research_ahj_recent_activity_jurisdiction_id_fkey"
            columns: ["jurisdiction_id"]
            isOneToOne: false
            referencedRelation: "research_ahj_topic_status_public"
            referencedColumns: ["jurisdiction_id"]
          },
        ]
      }
      research_ahj_tile_summary_public: {
        Row: {
          activity_by_tech: Json | null
          incentive_status: string | null
          jurisdiction_id: string | null
          last_synthesized_at: string | null
          moratorium_status: string | null
        }
        Relationships: [
          {
            foreignKeyName: "research_ahj_recent_activity_jurisdiction_id_fkey"
            columns: ["jurisdiction_id"]
            isOneToOne: false
            referencedRelation: "research_ahj"
            referencedColumns: ["jurisdiction_id"]
          },
          {
            foreignKeyName: "research_ahj_recent_activity_jurisdiction_id_fkey"
            columns: ["jurisdiction_id"]
            isOneToOne: false
            referencedRelation: "research_ahj_topic_status_public"
            referencedColumns: ["jurisdiction_id"]
          },
        ]
      }
      research_ahj_topic_skip: {
        Row: {
          citation_url: string | null
          defers_to_jurisdiction_id: string | null
          defers_to_level: Database["public"]["Enums"]["ahj_level_enum"] | null
          jurisdiction_id: string | null
          justification: string | null
          reason: Database["public"]["Enums"]["exclusion_reason_enum"] | null
          source: string | null
          topic: Database["public"]["Enums"]["topic_enum"] | null
        }
        Relationships: []
      }
      research_ahj_topic_status_public: {
        Row: {
          citation_url: string | null
          defers_to_government_type:
            | Database["public"]["Enums"]["government_type_enum"]
            | null
          defers_to_jurisdiction_id: string | null
          defers_to_level: Database["public"]["Enums"]["ahj_level_enum"] | null
          defers_to_name: string | null
          jurisdiction_id: string | null
          justification: string | null
          last_discovered_at: string | null
          last_sufficiency:
            | Database["public"]["Enums"]["sufficiency_enum"]
            | null
          next_eligible_at: string | null
          reason: Database["public"]["Enums"]["exclusion_reason_enum"] | null
          status: string | null
          topic: Database["public"]["Enums"]["topic_enum"] | null
        }
        Relationships: []
      }
    }
    Functions: {
      accept_invitation: { Args: { p_token: string }; Returns: string }
      add_concealed_workspace_member: {
        Args: {
          p_email: string
          p_role?: Database["public"]["Enums"]["user_role"]
          p_workspace_id: string
        }
        Returns: string
      }
      add_layer_column: {
        Args: {
          in_column_name: string
          in_is_read_only?: boolean
          in_layer_id: string
          in_position?: number
        }
        Returns: number
      }
      add_layer_columns: {
        Args: {
          in_column_names: string[]
          in_is_read_only?: boolean
          in_layer_id: string
        }
        Returns: number[]
      }
      add_layer_row: {
        Args: {
          in_feature_id?: string
          in_feature_match?: Database["public"]["Enums"]["feature_match_status"]
          in_geometry: unknown
          in_layer_id: string
          in_properties: Json
        }
        Returns: number
      }
      add_layer_row_dedup_on_property: {
        Args: {
          in_dedupe_column: string
          in_dedupe_value: string
          in_geometry: unknown
          in_layer_id: string
          in_properties: Json
        }
        Returns: number
      }
      add_layer_row_ensure_columns: {
        Args: {
          in_feature_id?: string
          in_feature_match?: Database["public"]["Enums"]["feature_match_status"]
          in_geometry: unknown
          in_layer_id: string
          in_properties: Json
        }
        Returns: number
      }
      add_layer_rows_bulk: {
        Args: {
          in_geometries: string[]
          in_layer_id: string
          in_properties: Json[]
        }
        Returns: number[]
      }
      admin_create_workspace: {
        Args: {
          p_name: string
          p_tier: Database["public"]["Enums"]["workspace_tier"]
          p_user_id: string
        }
        Returns: {
          id: string
          name: string
          slug: string
          tier: Database["public"]["Enums"]["workspace_tier"]
        }[]
      }
      admin_list_lab_targets: {
        Args: {
          p_limit?: number
          p_offset?: number
          p_search?: string
          p_user_id: string
        }
        Returns: {
          id: string
          name: string
          slug: string
          total_count: number
        }[]
      }
      admin_list_pipeline_sites: {
        Args: { p_pipeline_layer_id: string; p_user_id: string }
        Returns: {
          address: string
          geometry: Json
          name: string
          row_id: number
        }[]
      }
      admin_list_site_pipelines: {
        Args: { p_user_id: string; p_workspace_id: string }
        Returns: {
          id: string
          is_free_report: boolean
          site_count: number
          title: string
        }[]
      }
      admin_upgrade_workspaces_to_enterprise: {
        Args: { p_user_id: string; p_workspace_ids: string[] }
        Returns: {
          id: string
        }[]
      }
      attach_criteria_to_pipeline: {
        Args: {
          p_criterion_ids: string[]
          p_pipeline_layer_id: string
          p_workspace_id: string
        }
        Returns: {
          auto_evaluate: boolean
          basemap_style: string
          direction: string
          id: string
          is_shared: boolean
          methodology: string
          name: string
          parameters: Json
          scope: string
          scoring_rubric: Json
          search_radius_miles: number
          summary_metric_instructions: string
          summary_metric_type: string
          thresholds: Json
          unit: string
          zones: Json
        }[]
      }
      check_free_report_upload_rate_limit: {
        Args: {
          p_client_ip: unknown
          p_max_requests: number
          p_window_seconds: number
        }
        Returns: boolean
      }
      claim_export_finalize: {
        Args: { p_parent_job_id: string }
        Returns: boolean
      }
      claim_next_user_message: {
        Args: { p_thread_id: string }
        Returns: {
          claimed_at: string | null
          content: Json
          cost_is_per_turn: boolean
          cost_usd: number | null
          created_at: string
          id: string
          input_tokens: number | null
          output_tokens: number | null
          role: Database["public"]["Enums"]["agent_message_role"]
          sequence_number: number
          status: string
          thread_id: string
          updated_at: string
        }[]
        SetofOptions: {
          from: "*"
          to: "agent_messages"
          isOneToOne: false
          isSetofReturn: true
        }
      }
      clone_layer: {
        Args: {
          new_description: string
          new_title: string
          source_layer_id: string
        }
        Returns: string
      }
      create_invitation: {
        Args: {
          p_email: string
          p_role: Database["public"]["Enums"]["user_role"]
          p_workspace_id: string
        }
        Returns: string
      }
      create_layer_revision: {
        Args: {
          in_bbox?: unknown
          in_description?: string
          in_geometry_type?: Database["public"]["Enums"]["layer_geometry_type"]
          in_job_id?: string
          in_layer_id: string
          in_max_zoom?: number
          in_min_zoom?: number
          in_revision_id: string
          in_source_filename?: string
          in_stats?: Json
          in_user_id?: string
        }
        Returns: string
      }
      create_pipeline_criterion: {
        Args: {
          p_auto_evaluate?: boolean
          p_basemap_style?: string
          p_direction: string
          p_evaluation_graph?: Json
          p_map_framing?: string
          p_map_pdf_download_enabled?: boolean
          p_methodology: string
          p_name: string
          p_parameters?: Json
          p_pipeline_layer_id: string
          p_report_template?: Json
          p_scope?: string
          p_scoring_rubric?: Json
          p_search_radius_miles?: number
          p_summary_metric_instructions?: string
          p_summary_metric_type?: string
          p_thresholds: Json
          p_unit: string
          p_workspace_id: string
          p_zones: Json
        }
        Returns: {
          auto_evaluate: boolean
          direction: string
          id: string
          methodology: string
          name: string
          parameters: Json
          scope: string
          scoring_rubric: Json
          thresholds: Json
          unit: string
          zones: Json
        }[]
      }
      create_site_pipeline: {
        Args: { p_name: string; p_workspace_id: string }
        Returns: string
      }
      create_site_view: {
        Args: { p_name: string; p_workspace_id: string }
        Returns: {
          created_at: string
          created_by: string | null
          id: string
          is_default: boolean
          name: string
          position: number | null
          workspace_id: string
        }
        SetofOptions: {
          from: "*"
          to: "site_views"
          isOneToOne: true
          isSetofReturn: false
        }
      }
      create_workspace_api_key: {
        Args: { p_name: string; p_workspace_id: string }
        Returns: {
          api_key: string
          api_key_id: string
        }[]
      }
      decline_invitation: { Args: { p_token: string }; Returns: boolean }
      delete_integration_secret: {
        Args: { p_secret_id: string }
        Returns: boolean
      }
      delete_layer_column: {
        Args: { in_column_name: string; in_layer_id: string }
        Returns: undefined
      }
      delete_layer_row: { Args: { in_row_id: number }; Returns: undefined }
      delete_site_view: { Args: { p_view_id: string }; Returns: number }
      detach_criterion_from_pipeline: {
        Args: { p_criterion_id: string; p_pipeline_layer_id: string }
        Returns: {
          deleted_criterion: boolean
        }[]
      }
      diligence_criterion_render_fingerprints: {
        Args: { p_pairs: Json }
        Returns: {
          criterion_id: string
          fingerprint: string
          layer_row_id: number
        }[]
      }
      find_or_create_free_report_site_row: {
        Args: {
          p_caller_id?: string
          p_geometry: unknown
          p_intake_id: string
          p_layer_id: string
          p_properties: Json
        }
        Returns: {
          created: boolean
          row_id: number
        }[]
      }
      find_site_row_by_property: {
        Args: { p_column_name: string; p_layer_id: string; p_value: string }
        Returns: number
      }
      find_site_rows_by_feature_id: {
        Args: { p_feature_id: string; p_workspace_id: string }
        Returns: {
          layer_id: string
          row_id: number
        }[]
      }
      fork_pipeline_criterion: {
        Args: {
          p_auto_evaluate?: boolean
          p_basemap_style?: string
          p_criterion_id: string
          p_direction: string
          p_evaluation_graph_enabled?: boolean
          p_map_framing?: string
          p_map_pdf_download_enabled?: boolean
          p_methodology: string
          p_name: string
          p_parameters?: Json
          p_pipeline_layer_id: string
          p_report_template?: Json
          p_report_template_provided?: boolean
          p_scope?: string
          p_scoring_rubric?: Json
          p_search_radius_miles?: number
          p_thresholds: Json
          p_unit: string
          p_zones: Json
        }
        Returns: {
          auto_evaluate: boolean
          direction: string
          id: string
          methodology: string
          name: string
          parameters: Json
          scope: string
          scoring_rubric: Json
          summary_metric_instructions: string
          summary_metric_type: string
          thresholds: Json
          unit: string
          zones: Json
        }[]
      }
      generate_workspace_slug: { Args: { name: string }; Returns: string }
      get_current_user_sso_policy: {
        Args: never
        Returns: {
          enforce_sso: boolean
          is_bypass: boolean
          provider: string
        }[]
      }
      get_free_report_usage: {
        Args: { p_workspace_id: string }
        Returns: number
      }
      get_integration_secret: { Args: { p_secret_id: string }; Returns: string }
      get_invitation: {
        Args: { p_token: string }
        Returns: {
          invited_by_name: string
          invitee_email: string
          invitee_role: Database["public"]["Enums"]["user_role"]
          workspace_id: string
          workspace_name: string
          workspace_slug: string
        }[]
      }
      get_layer_data: {
        Args: { layer_id: string }
        Returns: {
          feature_id: string
          feature_match: Database["public"]["Enums"]["feature_match_status"]
          geometry: Json
          properties: Json
          row_id: number
        }[]
      }
      get_layer_geojson: { Args: { layer_id: string }; Returns: Json }
      get_layer_row_extent: {
        Args: { p_layer_row_id: number }
        Returns: number[]
      }
      get_pipeline_site_views: {
        Args: { p_layer_id: string }
        Returns: {
          row_id: number
          site_view_id: string
        }[]
      }
      get_pipeline_summaries: {
        Args: { p_workspace_id: string }
        Returns: {
          created_at: string
          criteria_count: number
          layer_id: string
          name: string
          site_count: number
        }[]
      }
      get_raster_assets: {
        Args: {
          p_dataset_id: string
          p_max_lat: number
          p_max_lon: number
          p_min_lat: number
          p_min_lon: number
        }
        Returns: {
          source_id: string
        }[]
      }
      get_raw_layer_data: {
        Args: { layer_id: string }
        Returns: {
          feature_id: string
          feature_match: Database["public"]["Enums"]["feature_match_status"]
          properties: Json
          row_id: number
        }[]
      }
      get_shared_free_report: {
        Args: { p_report_id: string }
        Returns: {
          criteria: Json
          pipeline_layer_id: string
          pipeline_name: string
          site: Json
          theme: string
          workspace_id: string
        }[]
      }
      get_site_diligence_data: {
        Args: { p_layer_id: string }
        Returns: {
          criteria_results: Json
          feature_id: string
          geometry: Json
          properties: Json
          row_id: number
          verdict_updated_at: string
        }[]
      }
      get_sso_login_policy_for_email: {
        Args: { p_email: string }
        Returns: {
          provider: string
          sso_required: boolean
        }[]
      }
      get_sso_provider_for_slug: { Args: { p_slug: string }; Returns: string }
      get_workspace_creator_emails: {
        Args: { p_workspace_ids: string[] }
        Returns: {
          creator_email: string
          workspace_id: string
        }[]
      }
      get_workspace_daily_usage: {
        Args: { p_end: string; p_start: string; p_workspace_id: string }
        Returns: {
          cost_usd: number
          date: string
          input_tokens: number
          message_count: number
          output_tokens: number
        }[]
      }
      get_workspace_layer_usage: {
        Args: { p_workspace_id: string }
        Returns: Json
      }
      get_workspace_spend_cap_headroom: {
        Args: { p_workspace_id: string }
        Returns: {
          cap_reached: boolean
          percent_left: number
        }[]
      }
      get_workspace_spend_cap_status: {
        Args: { p_user_id: string; p_workspace_id: string }
        Returns: {
          alert_enabled: boolean
          cap_reached: boolean
          cap_reached_alerted_at: string
          cap_usd: number
          counted_since: string
          counted_sources: string[]
          enabled: boolean
          low_balance_alerted_at: string
          remaining_usd: number
          spent_usd: number
        }[]
      }
      initialize_site_layer: {
        Args: { p_workspace_id: string }
        Returns: string
      }
      is_current_user_staff: { Args: never; Returns: boolean }
      is_email_domain_blocked: { Args: { p_email: string }; Returns: boolean }
      is_user_registered: { Args: { p_email: string }; Returns: boolean }
      join_sso_workspace: { Args: never; Returns: string }
      link_folder_to_map: {
        Args: { p_folder_id: string; p_map_id: string }
        Returns: string
      }
      link_layer_to_map: {
        Args: { p_layer_id: string; p_map_id: string }
        Returns: number
      }
      list_admin_workspaces: {
        Args: {
          p_limit?: number
          p_offset?: number
          p_search?: string
          p_tier?: Database["public"]["Enums"]["workspace_tier"]
          p_user_id: string
        }
        Returns: {
          created_at: string
          creator_email: string
          id: string
          last_report_at: string
          name: string
          report_count: number
          slug: string
          tier: Database["public"]["Enums"]["workspace_tier"]
          total_count: number
          viewer_role: Database["public"]["Enums"]["user_role"]
        }[]
      }
      list_lab_installations: {
        Args: { p_source_criterion_ids: string[]; p_user_id: string }
        Returns: {
          clone_id: string
          display_differences: Json
          pipelines: Json
          published_at: string
          published_by_email: string
          recipe_modified_by_client: boolean
          recipe_update_available: boolean
          source_criterion_id: string
          workspace_id: string
          workspace_name: string
        }[]
      }
      list_workspace_criteria_for_pipeline: {
        Args: { p_pipeline_layer_id: string; p_workspace_id: string }
        Returns: {
          auto_evaluate: boolean
          basemap_style: string
          direction: string
          id: string
          is_shared: boolean
          map_framing: string
          map_pdf_download_enabled: boolean
          methodology: string
          name: string
          parameters: Json
          pipeline_count: number
          scope: string
          scoring_rubric: Json
          search_radius_miles: number
          summary_metric_instructions: string
          summary_metric_type: string
          thresholds: Json
          unit: string
          zones: Json
        }[]
      }
      move_sites_to_view: {
        Args: { p_layer_id: string; p_row_ids: number[]; p_view_id: string }
        Returns: undefined
      }
      propagate_poi_binding: {
        Args: { p_criterion_id: string }
        Returns: string[]
      }
      provision_free_report_pipeline: {
        Args: { p_theme: string; p_workspace_id: string }
        Returns: Json
      }
      publish_lab_criterion: {
        Args: {
          p_overwrite?: boolean
          p_pipeline_layer_ids: string[]
          p_source_criterion_id: string
          p_user_id: string
          p_workspace_id: string
        }
        Returns: Json
      }
      publish_lab_pipeline: {
        Args: {
          p_new_pipeline_name?: string
          p_overwrite_criterion_ids?: string[]
          p_source_pipeline_id: string
          p_target_pipeline_id?: string
          p_user_id: string
          p_workspace_id: string
        }
        Returns: Json
      }
      remove_concealed_workspace_member: {
        Args: { p_email: string; p_workspace_id: string }
        Returns: boolean
      }
      rename_layer_column: {
        Args: {
          in_column_name: string
          in_layer_id: string
          in_new_column_name: string
        }
        Returns: undefined
      }
      rename_site_view: {
        Args: { p_name: string; p_view_id: string }
        Returns: undefined
      }
      reorder_pipeline_criteria: {
        Args: {
          p_criterion_ids: string[]
          p_pipeline_layer_id: string
          p_workspace_id: string
        }
        Returns: undefined
      }
      replace_map_layers: {
        Args: { p_layer_ids: string[]; p_map_id: string }
        Returns: undefined
      }
      research_ahj_promote_reclassified_copies: {
        Args: { p_reason: string }
        Returns: number
      }
      resend_invitation: { Args: { p_invitation_id: string }; Returns: boolean }
      revoke_invitation: { Args: { p_invitation_id: string }; Returns: boolean }
      set_workspace_feature: {
        Args: { p_enabled: boolean; p_feature: string; p_workspace_id: string }
        Returns: Json
      }
      set_workspace_spend_cap: {
        Args: {
          p_alert_enabled?: boolean
          p_cap_usd?: number
          p_counted_sources?: string[]
          p_enabled: boolean
          p_user_id: string
          p_workspace_id: string
        }
        Returns: {
          alert_enabled: boolean
          cap_reached_alerted_at: string | null
          cap_usd: number
          counted_since: string
          counted_sources: string[]
          created_at: string
          created_by: string | null
          enabled: boolean
          low_balance_alerted_at: string | null
          spent_usd: number
          updated_at: string
          updated_by: string | null
          workspace_id: string
        }
        SetofOptions: {
          from: "*"
          to: "workspace_spend_caps"
          isOneToOne: true
          isSetofReturn: false
        }
      }
      sso_access_token_hook: { Args: { event: Json }; Returns: Json }
      store_integration_secret: {
        Args: {
          p_secret_name: string
          p_secret_value: string
          p_workspace_id: string
        }
        Returns: string
      }
      sync_free_report_theme: {
        Args: {
          p_dry_run?: boolean
          p_excluded_source_criterion_ids?: string[]
          p_source_pipeline_id: string
          p_theme: string
        }
        Returns: Json
      }
      update_graph_result: {
        Args: {
          p_criterion_id: string
          p_layer_row_id: number
          p_patch: Json
          p_session_id: string
          p_thread_id: string
        }
        Returns: boolean
      }
      update_layer_feature_match: {
        Args: {
          in_boundary_id: string
          in_layer_id: string
          in_matched_columns: Json
        }
        Returns: undefined
      }
      update_layer_row_geometry: {
        Args: { in_geometry: unknown; in_row_id: number }
        Returns: undefined
      }
      update_layer_row_match: {
        Args: {
          in_feature_id: string
          in_row_id: number
          in_status: Database["public"]["Enums"]["feature_match_status"]
        }
        Returns: undefined
      }
      update_layer_row_properties: {
        Args: { in_properties: Json; in_row_id: number }
        Returns: undefined
      }
      update_layer_row_properties_ensure_columns: {
        Args: { in_properties: Json; in_row_id: number }
        Returns: undefined
      }
      upsert_agent_data_source: {
        Args: { p_status?: string; p_url: string; p_usage_notes?: string }
        Returns: {
          access_count: number
          created_at: string
          last_verified_at: string | null
          status: string
          updated_at: string
          url: string
          usage_notes: string | null
        }
        SetofOptions: {
          from: "*"
          to: "agent_data_sources"
          isOneToOne: true
          isSetofReturn: false
        }
      }
      validate_workspace_api_key: {
        Args: { p_api_key: string }
        Returns: {
          api_key_id: string
          role: Database["public"]["Enums"]["user_role"]
          user_id: string
          workspace_id: string
          workspace_tier: Database["public"]["Enums"]["workspace_tier"]
        }[]
      }
      workspace_spend_cap_blocks: {
        Args: {
          p_agent_type?: string
          p_thread_id?: string
          p_workspace_id: string
        }
        Returns: boolean
      }
    }
    Enums: {
      agent_message_role: "user" | "assistant"
      ahj_level_enum: "county" | "state" | "federal"
      batch_status_enum:
        | "validating"
        | "failed"
        | "in_progress"
        | "finalizing"
        | "completed"
        | "expired"
        | "cancelling"
        | "cancelled"
      capture_integrity_enum: "verified" | "partial" | "unverified" | "failed"
      capture_kind_enum:
        | "pdf"
        | "html_snapshot"
        | "screenshot"
        | "office_doc"
        | "gis_export"
        | "api_json"
        | "image_raster"
      capture_status_enum:
        | "captured"
        | "http_error"
        | "blocked"
        | "timeout"
        | "skipped"
        | "pending"
        | "superseded"
      capture_tool_enum:
        | "requests"
        | "pymupdf"
        | "headless"
        | "municode_api"
        | "ecode360_api"
        | "curl_cffi"
        | "site_unblocker"
      core_layer_processing_mode: "cloud_run" | "batch"
      discovery_model_enum: "haiku" | "sonnet"
      doc_role_enum:
        | "ordinance_text"
        | "dimensional_table"
        | "fee_schedule"
        | "application_form"
        | "nav_page"
        | "gis_layer"
        | "news"
        | "other"
      doc_type_enum:
        | "ordinance_code"
        | "pdf_document"
        | "gis_map"
        | "council_minutes"
        | "council_agenda"
        | "staff_report"
        | "press_article"
        | "webpage"
        | "dataset"
        | "other"
      endonym_confidence_enum: "high" | "medium"
      evidence_confidence_enum: "high" | "medium" | "low"
      exclusion_reason_enum:
        | "no_authority"
        | "defers_to"
        | "consistently_empty"
        | "permanently_blocked"
        | "manual"
        | "out_of_scope"
      feature_match_status: "ok" | "not_found"
      government_type_enum:
        | "City"
        | "Town"
        | "County"
        | "Township"
        | "Borough"
        | "Village"
        | "Tribal Government"
        | "State Agency"
        | "Federal Agency"
      integration_provider: "salesforce" | "pipedrive" | "hubspot"
      invitation_status: "pending" | "accepted" | "declined" | "expired"
      job_status: "uploading" | "processing" | "failed" | "completed"
      jurisdiction_match_enum:
        | "confirmed"
        | "probable"
        | "ambiguous"
        | "mismatch"
        | "unchecked"
      known_code_host_enum:
        | "municode"
        | "ecode360"
        | "amlegal"
        | "none"
        | "unknown"
      lane_enum: "etl" | "pipeline" | "map"
      layer_builder_review_status: "backlog" | "passing" | "failed"
      layer_builder_row_status:
        | "not_built"
        | "building"
        | "completed"
        | "awaiting_input"
        | "error"
      layer_geometry_type: "point" | "polygon" | "line_string"
      layer_status: "processing" | "ok" | "error"
      layer_type:
        | "standard"
        | "parcel"
        | "tileset"
        | "integration_sync"
        | "site"
        | "core"
        | "criterion_analysis"
        | "raster"
      liveness_enum: "live" | "moved" | "dead"
      priority_tier_enum: "A" | "B" | "C"
      project_type_enum:
        | "utility_scale"
        | "commercial"
        | "colocation"
        | "residential"
        | "smr"
        | "mixed_use"
      raster_dataset_kind: "elevation"
      run_status_enum:
        | "running"
        | "success"
        | "partial"
        | "failed"
        | "awaiting_batch"
      source_authority_enum:
        | "primary_official"
        | "official_code_host"
        | "secondary_official"
        | "third_party"
        | "press"
        | "unknown"
      source_host_kind_enum:
        | "primary_gov"
        | "civic_cms"
        | "code_host_municode"
        | "code_host_amlegal"
        | "code_host_ecode360"
        | "code_host_codepublishing"
        | "code_host_other"
        | "agenda_records"
        | "permit_portal"
        | "gis_portal"
        | "news_media"
        | "third_party"
        | "unknown"
      staleness_enum: "fresh" | "stale" | "superseded" | "dead"
      sufficiency_enum: "strong" | "thin" | "none"
      sync_status:
        | "pending_initial_sync"
        | "syncing"
        | "active"
        | "paused"
        | "error"
      tech_category_enum:
        | "data_center"
        | "solar"
        | "wind"
        | "bess"
        | "nuclear"
        | "housing"
        | "general"
      tileset_format: "zxy" | "pmtiles"
      topic_enum:
        | "zoning"
        | "permitting"
        | "building_code"
        | "moratorium"
        | "ahj"
        | "ccr"
        | "design_codes"
        | "incentives"
        | "recent_ahj_activity"
      topic_run_status_enum:
        | "pending"
        | "discovered"
        | "captured"
        | "failed"
        | "excluded"
      trigger_kind_enum: "scheduled" | "targeted"
      user_role: "owner" | "editor" | "viewer" | "analyst"
      workspace_tier: "free" | "enterprise"
      zoning_map_format_enum: "vector" | "raster"
    }
    CompositeTypes: {
      [_ in never]: never
    }
  }
}

type DatabaseWithoutInternals = Omit<Database, "__InternalSupabase">

type DefaultSchema = DatabaseWithoutInternals[Extract<keyof Database, "public">]

export type Tables<
  DefaultSchemaTableNameOrOptions extends
    | keyof (DefaultSchema["Tables"] & DefaultSchema["Views"])
    | { schema: keyof DatabaseWithoutInternals },
  TableName extends DefaultSchemaTableNameOrOptions extends {
    schema: keyof DatabaseWithoutInternals
  }
    ? keyof (DatabaseWithoutInternals[DefaultSchemaTableNameOrOptions["schema"]]["Tables"] &
        DatabaseWithoutInternals[DefaultSchemaTableNameOrOptions["schema"]]["Views"])
    : never = never,
> = DefaultSchemaTableNameOrOptions extends {
  schema: keyof DatabaseWithoutInternals
}
  ? (DatabaseWithoutInternals[DefaultSchemaTableNameOrOptions["schema"]]["Tables"] &
      DatabaseWithoutInternals[DefaultSchemaTableNameOrOptions["schema"]]["Views"])[TableName] extends {
      Row: infer R
    }
    ? R
    : never
  : DefaultSchemaTableNameOrOptions extends keyof (DefaultSchema["Tables"] &
        DefaultSchema["Views"])
    ? (DefaultSchema["Tables"] &
        DefaultSchema["Views"])[DefaultSchemaTableNameOrOptions] extends {
        Row: infer R
      }
      ? R
      : never
    : never

export type TablesInsert<
  DefaultSchemaTableNameOrOptions extends
    | keyof DefaultSchema["Tables"]
    | { schema: keyof DatabaseWithoutInternals },
  TableName extends DefaultSchemaTableNameOrOptions extends {
    schema: keyof DatabaseWithoutInternals
  }
    ? keyof DatabaseWithoutInternals[DefaultSchemaTableNameOrOptions["schema"]]["Tables"]
    : never = never,
> = DefaultSchemaTableNameOrOptions extends {
  schema: keyof DatabaseWithoutInternals
}
  ? DatabaseWithoutInternals[DefaultSchemaTableNameOrOptions["schema"]]["Tables"][TableName] extends {
      Insert: infer I
    }
    ? I
    : never
  : DefaultSchemaTableNameOrOptions extends keyof DefaultSchema["Tables"]
    ? DefaultSchema["Tables"][DefaultSchemaTableNameOrOptions] extends {
        Insert: infer I
      }
      ? I
      : never
    : never

export type TablesUpdate<
  DefaultSchemaTableNameOrOptions extends
    | keyof DefaultSchema["Tables"]
    | { schema: keyof DatabaseWithoutInternals },
  TableName extends DefaultSchemaTableNameOrOptions extends {
    schema: keyof DatabaseWithoutInternals
  }
    ? keyof DatabaseWithoutInternals[DefaultSchemaTableNameOrOptions["schema"]]["Tables"]
    : never = never,
> = DefaultSchemaTableNameOrOptions extends {
  schema: keyof DatabaseWithoutInternals
}
  ? DatabaseWithoutInternals[DefaultSchemaTableNameOrOptions["schema"]]["Tables"][TableName] extends {
      Update: infer U
    }
    ? U
    : never
  : DefaultSchemaTableNameOrOptions extends keyof DefaultSchema["Tables"]
    ? DefaultSchema["Tables"][DefaultSchemaTableNameOrOptions] extends {
        Update: infer U
      }
      ? U
      : never
    : never

export type Enums<
  DefaultSchemaEnumNameOrOptions extends
    | keyof DefaultSchema["Enums"]
    | { schema: keyof DatabaseWithoutInternals },
  EnumName extends DefaultSchemaEnumNameOrOptions extends {
    schema: keyof DatabaseWithoutInternals
  }
    ? keyof DatabaseWithoutInternals[DefaultSchemaEnumNameOrOptions["schema"]]["Enums"]
    : never = never,
> = DefaultSchemaEnumNameOrOptions extends {
  schema: keyof DatabaseWithoutInternals
}
  ? DatabaseWithoutInternals[DefaultSchemaEnumNameOrOptions["schema"]]["Enums"][EnumName]
  : DefaultSchemaEnumNameOrOptions extends keyof DefaultSchema["Enums"]
    ? DefaultSchema["Enums"][DefaultSchemaEnumNameOrOptions]
    : never

export type CompositeTypes<
  PublicCompositeTypeNameOrOptions extends
    | keyof DefaultSchema["CompositeTypes"]
    | { schema: keyof DatabaseWithoutInternals },
  CompositeTypeName extends PublicCompositeTypeNameOrOptions extends {
    schema: keyof DatabaseWithoutInternals
  }
    ? keyof DatabaseWithoutInternals[PublicCompositeTypeNameOrOptions["schema"]]["CompositeTypes"]
    : never = never,
> = PublicCompositeTypeNameOrOptions extends {
  schema: keyof DatabaseWithoutInternals
}
  ? DatabaseWithoutInternals[PublicCompositeTypeNameOrOptions["schema"]]["CompositeTypes"][CompositeTypeName]
  : PublicCompositeTypeNameOrOptions extends keyof DefaultSchema["CompositeTypes"]
    ? DefaultSchema["CompositeTypes"][PublicCompositeTypeNameOrOptions]
    : never

export const Constants = {
  graphql_public: {
    Enums: {},
  },
  public: {
    Enums: {
      agent_message_role: ["user", "assistant"],
      ahj_level_enum: ["county", "state", "federal"],
      batch_status_enum: [
        "validating",
        "failed",
        "in_progress",
        "finalizing",
        "completed",
        "expired",
        "cancelling",
        "cancelled",
      ],
      capture_integrity_enum: ["verified", "partial", "unverified", "failed"],
      capture_kind_enum: [
        "pdf",
        "html_snapshot",
        "screenshot",
        "office_doc",
        "gis_export",
        "api_json",
        "image_raster",
      ],
      capture_status_enum: [
        "captured",
        "http_error",
        "blocked",
        "timeout",
        "skipped",
        "pending",
        "superseded",
      ],
      capture_tool_enum: [
        "requests",
        "pymupdf",
        "headless",
        "municode_api",
        "ecode360_api",
        "curl_cffi",
        "site_unblocker",
      ],
      core_layer_processing_mode: ["cloud_run", "batch"],
      discovery_model_enum: ["haiku", "sonnet"],
      doc_role_enum: [
        "ordinance_text",
        "dimensional_table",
        "fee_schedule",
        "application_form",
        "nav_page",
        "gis_layer",
        "news",
        "other",
      ],
      doc_type_enum: [
        "ordinance_code",
        "pdf_document",
        "gis_map",
        "council_minutes",
        "council_agenda",
        "staff_report",
        "press_article",
        "webpage",
        "dataset",
        "other",
      ],
      endonym_confidence_enum: ["high", "medium"],
      evidence_confidence_enum: ["high", "medium", "low"],
      exclusion_reason_enum: [
        "no_authority",
        "defers_to",
        "consistently_empty",
        "permanently_blocked",
        "manual",
        "out_of_scope",
      ],
      feature_match_status: ["ok", "not_found"],
      government_type_enum: [
        "City",
        "Town",
        "County",
        "Township",
        "Borough",
        "Village",
        "Tribal Government",
        "State Agency",
        "Federal Agency",
      ],
      integration_provider: ["salesforce", "pipedrive", "hubspot"],
      invitation_status: ["pending", "accepted", "declined", "expired"],
      job_status: ["uploading", "processing", "failed", "completed"],
      jurisdiction_match_enum: [
        "confirmed",
        "probable",
        "ambiguous",
        "mismatch",
        "unchecked",
      ],
      known_code_host_enum: [
        "municode",
        "ecode360",
        "amlegal",
        "none",
        "unknown",
      ],
      lane_enum: ["etl", "pipeline", "map"],
      layer_builder_review_status: ["backlog", "passing", "failed"],
      layer_builder_row_status: [
        "not_built",
        "building",
        "completed",
        "awaiting_input",
        "error",
      ],
      layer_geometry_type: ["point", "polygon", "line_string"],
      layer_status: ["processing", "ok", "error"],
      layer_type: [
        "standard",
        "parcel",
        "tileset",
        "integration_sync",
        "site",
        "core",
        "criterion_analysis",
        "raster",
      ],
      liveness_enum: ["live", "moved", "dead"],
      priority_tier_enum: ["A", "B", "C"],
      project_type_enum: [
        "utility_scale",
        "commercial",
        "colocation",
        "residential",
        "smr",
        "mixed_use",
      ],
      raster_dataset_kind: ["elevation"],
      run_status_enum: [
        "running",
        "success",
        "partial",
        "failed",
        "awaiting_batch",
      ],
      source_authority_enum: [
        "primary_official",
        "official_code_host",
        "secondary_official",
        "third_party",
        "press",
        "unknown",
      ],
      source_host_kind_enum: [
        "primary_gov",
        "civic_cms",
        "code_host_municode",
        "code_host_amlegal",
        "code_host_ecode360",
        "code_host_codepublishing",
        "code_host_other",
        "agenda_records",
        "permit_portal",
        "gis_portal",
        "news_media",
        "third_party",
        "unknown",
      ],
      staleness_enum: ["fresh", "stale", "superseded", "dead"],
      sufficiency_enum: ["strong", "thin", "none"],
      sync_status: [
        "pending_initial_sync",
        "syncing",
        "active",
        "paused",
        "error",
      ],
      tech_category_enum: [
        "data_center",
        "solar",
        "wind",
        "bess",
        "nuclear",
        "housing",
        "general",
      ],
      tileset_format: ["zxy", "pmtiles"],
      topic_enum: [
        "zoning",
        "permitting",
        "building_code",
        "moratorium",
        "ahj",
        "ccr",
        "design_codes",
        "incentives",
        "recent_ahj_activity",
      ],
      topic_run_status_enum: [
        "pending",
        "discovered",
        "captured",
        "failed",
        "excluded",
      ],
      trigger_kind_enum: ["scheduled", "targeted"],
      user_role: ["owner", "editor", "viewer", "analyst"],
      workspace_tier: ["free", "enterprise"],
      zoning_map_format_enum: ["vector", "raster"],
    },
  },
} as const

